import { Component, OnInit, OnDestroy, ViewChild, ElementRef, signal, computed, AfterViewInit, inject } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';
import { AvatarModule } from 'primeng/avatar';
import { Router, ActivatedRoute } from '@angular/router';
import { ImageUploadService } from '../../../services/image-upload.service';
import { Store } from '@ngxs/store';
import { ResetUploadState, ImageUploadSelectors } from '../../../store/image-upload';
import { SetTagNotFound, SetQRCode, SetTenantId, SetAuthenticated, SetPublicScanResults } from '../../../store/app.actions';
import { AppStateSelectors } from '../../../store/app.selectors';
import { PrivacyService } from '../../services/privacy.service';
import QrScanner from 'qr-scanner';
import Pica from 'pica';
import { CAMERA_CONFIG } from './camera-scanner.config';
import type {
  CameraPermissions,
  ImageVariants,
  QRDetectionBox
} from './camera-scanner.types';
import { extractQrCodeAndTenant, resolveScanTenantId } from '../../utils/tenant-mapping';



@Component({
  selector: 'app-camera-scanner',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ButtonModule, CardModule, InputTextModule,
    ProgressSpinnerModule, DialogModule, AvatarModule,
  ],

  templateUrl: './camera-scanner.component.html',
  styleUrl: './camera-scanner.component.scss',
})
export class CameraScannerComponent implements OnInit, OnDestroy, AfterViewInit {
  // Pica instance for high-quality image processing
  private pica = new Pica({
    features: ['wasm', 'js'],
    idle: 1000,
    concurrency: 4
  });


  @ViewChild('videoElement', { static: false }) videoElementRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement', { static: false }) canvasElementRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('qrCanvas', { static: false }) qrCanvasRef!: ElementRef<HTMLCanvasElement>;

  // Core signals
  stream = signal<MediaStream | null>(null);
  videoElement = signal<HTMLVideoElement | null>(null);
  canvasElement = signal<HTMLCanvasElement | null>(null);
  context = signal<CanvasRenderingContext2D | null>(null);
  qrCanvas = signal<HTMLCanvasElement | null>(null);
  qrContext = signal<CanvasRenderingContext2D | null>(null);
  capturedImage = signal<string | null>(null);
  currentLocation = signal<GeolocationPosition | null>(null);
  uploadError = signal<string | null>(null);



  // QR Detection control
  private currentDetectionFrameId: number | null = null;
  private detectionStartTimeoutId: any = null;
  private captureAfterDetectionTimeoutId: any = null;

  // Optimized detection canvas (cached for performance)
  private detectionCanvas: HTMLCanvasElement | null = null;
  private detectionContext: CanvasRenderingContext2D | null = null;

  // Resize monitoring
  private resizeTimeout: any;

  // State signals
  isCameraActive = signal(false);
  isCapturing = signal(false);
  isProcessing = signal(false);
  isScanning = signal(false);
  permissions = signal<CameraPermissions>({ camera: 'prompt', location: 'prompt' });
  showCameraPermissionPopup = signal(false);
  isRequestingPermission = signal(false);

  // Upload retry logic
  uploadRetryCount = signal(0);
  maxUploadRetries = CAMERA_CONFIG.DETECTION.MAX_RETRIES;
  isUploading = signal(false);
  isRetrying = signal(false);

  // Scan completion state
  scanCompleted = signal<boolean>(false);

  // Redirect state for non-mobile screens
  isRedirecting = signal<boolean>(false);

  // Tag not found state
  isTagNotFound = signal<boolean>(false);

  // QR Code from state - will be initialized in constructor
  qrCode: any; // Type according to store
  tenantId: any; // Type according to store

  // Torch/Flashlight functionality
  isTorchSupported = signal<boolean>(false);
  isTorchOn = signal<boolean>(false);
  currentTrack: MediaStreamTrack | null = null;

  // Current tenant ID value
  currentTenantId = signal<string>('regenesis');

  // QR Code detection box properties
  qrDetectionBox = signal<QRDetectionBox | null>(null);
  qrBoxColor = signal<string>('red'); // red = no detection, green = detected
  qrBoxOpacity = signal<number>(0.8);
  showQRBox = signal<boolean>(true);

  // Camera zoom properties
  currentZoom = signal<number>(1.0);
  minZoom = signal<number>(CAMERA_CONFIG.ZOOM.MIN_LEVEL);
  maxZoom = signal<number>(CAMERA_CONFIG.ZOOM.MAX_LEVEL);
  defaultZoom = signal<number>(2.0); // Default zoom for better small QR detection
  isZoomSupported = signal<boolean>(false);
  showZoomControls = signal<boolean>(false);

  // Laava aesthetic zoom value (typically 240)
  laavaZoom = signal<number>(240);

  // Image variants storage (typed)
  private imageVariants: ImageVariants | null = null;

  // Essential QR detection properties
  qrDetectionTriggered = signal<boolean>(false);

  // Detection status for user feedback
  detectionStatus = signal<string>('Ready to scan');

  // Camera error type for specific messaging
  cameraErrorType = signal<'in-use' | 'not-found' | 'denied' | 'unknown' | null>(null);
  detectionAttempts = signal<number>(0);

  // Concurrent session state
  private readonly sessionCompletedSubject = new BehaviorSubject<boolean>(false);
  private sessionCompleted = false;
  private readonly uploadResults: any[] = [];
  private readonly uploadSubs = new Map<number, Subscription>();
  private readonly REQUIRED_USABLE_FRAMES = 3;

  // Actual detected QR coordinates (in display coordinates) - used for capture
  // Separate from qrDetectionBox which is the fixed visual box
  actualQRBox = signal<{ x: number; y: number; width: number; height: number } | null>(null);

  // Anti-false-positive mechanism (mobile-optimized)
  private lastValidDetection = 0;
  private detectionDebounceTime = CAMERA_CONFIG.DETECTION.DEBOUNCE_MS; // 300ms
  private consecutiveDetections = 0;
  private requiredConsecutiveDetections = 1; // Immediate response
  private lastImageDataTime = 0; // Track last getImageData call
  private imageDataThrottle = 33; // ~30fps for better performance (optimized from 60fps)

  // Computed values
  canAccessCamera = computed(() => this.permissions().camera === 'granted');

  // Mobile detection
  isMobileDevice = computed(() => this.checkIfMobile());

  // Tab visibility tracking
  private isTabVisible = true;

  // Idle timeout tracking
  private lastActivityTime = Date.now();
  private idleTimer: any = null;
  private idleWarningTimer: any = null;
  showIdleWarning = signal<boolean>(false);
  idleTimeRemaining = signal<number>(0);
  private qrDetectionTimer: any = null;

  private checkIfMobile(): boolean {
    // Check screen width - only allow mobile phone sizes (≤480px)
    if (window.innerWidth <= 480) {
      return true;
    }

    // Check user agent for mobile devices
    const userAgent = navigator.userAgent.toLowerCase();
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  }

  private setupTabVisibilityTracking(): void {
    // Pause detection when tab is not visible to save resources
    document.addEventListener('visibilitychange', () => {
      this.isTabVisible = !document.hidden;
    });
  }

  private monitorScreenSizeChanges(): void {
    // Add window resize listener to monitor screen size changes
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  private handleResize(): void {
    // Immediate check for large screen size changes (mobile to tablet/desktop)
    const currentWidth = window.innerWidth;
    if (currentWidth > 480) {
      // Set redirecting state and redirect immediately
      this.isRedirecting.set(true);
      this.stopCamera();
      this.router.navigate(['/mobile-only'], {
        queryParams: { returnUrl: '/scanner' }
      });
      return; // Exit early to avoid the timeout
    }

    // Reset redirect state if we're back to mobile
    this.isRedirecting.set(false);

    // Clear any existing timeout for smaller changes
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    // Debounce for smaller changes that might still need checking
    this.resizeTimeout = setTimeout(() => {
      // Check if device is no longer mobile (double-check with full logic)
      if (!this.checkIfMobile()) {
        this.isRedirecting.set(true);
        this.stopCamera();
        this.router.navigate(['/mobile-only'], {
          queryParams: { returnUrl: '/scanner' }
        });
      }
    }, 100);
  }

  public readonly imageUploadService = inject(ImageUploadService);
  private readonly store = inject(Store);
  private readonly privacyService = inject(PrivacyService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  constructor() {
    // Initialize store selectors
    // Initialize store selectors
    this.qrCode = this.store.select(AppStateSelectors.getQRCode);
    this.tenantId = this.store.select(AppStateSelectors.getTenantId);
  }

  ngOnInit(): void {
    // Get tenant ID from state
    this.tenantId.subscribe((tenantId: any) => {
      if (tenantId) {
        this.currentTenantId.set(tenantId);
      }
    });

    // Check permissions and handle camera permission request/popup
    this.checkPermissions().then(() => {
      // After permission check completes, check if we need to request or show popup
      this.checkAndShowCameraPermissionPopup();
    });

    this.getCurrentLocation();
    this.store.select(ImageUploadSelectors.getError).subscribe(error => {
      if (error !== undefined) {
        this.uploadError.set(error);
      }
    });

    // Monitor screen size changes and redirect to mobile-only if becomes desktop
    this.monitorScreenSizeChanges();

    // Setup tab visibility tracking for resource optimization
    this.setupTabVisibilityTracking();
  }

  private getCurrentLocation(): void {
    if (navigator.geolocation && this.privacyService.hasConsentedToLocation()) {
      navigator.geolocation.getCurrentPosition(
        position => {
          this.currentLocation.set(position);
        },
        _error => {
          // Location error handled silently
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    }
  }

  ngAfterViewInit(): void {
    // Initialize canvas element with performance optimization
    if (this.canvasElementRef) {
      const canvas = this.canvasElementRef.nativeElement;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      this.canvasElement.set(canvas);
      this.context.set(ctx);
    }

    // Initialize QR overlay canvas with performance optimization
    if (this.qrCanvasRef) {
      const qrCanvas = this.qrCanvasRef.nativeElement;
      const qrCtx = qrCanvas.getContext('2d', { willReadFrequently: true });
      this.qrCanvas.set(qrCanvas);
      this.qrContext.set(qrCtx);
    }

    // Ensure permission check happens after view init
    // This is a fallback in case ngOnInit hasn't completed yet
    setTimeout(() => {
      const cameraState = this.permissions().camera;
      // Only check if permission is still in prompt state (not yet handled)
      if (cameraState === 'prompt' || cameraState === 'denied') {
        this.checkAndShowCameraPermissionPopup();
      }
    }, 200);
  }

  private async checkAndShowCameraPermissionPopup(): Promise<void> {
    const cameraState = this.permissions().camera;

    // If permission is already granted, nothing to do
    if (cameraState === 'granted') {
      return;
    }

    // If permission is 'prompt' (default), request it immediately
    // This triggers the browser's native permission dialog
    if (cameraState === 'prompt') {
      await this.requestCameraPermission();
    }
    // If permission is 'denied' or explicitly blocked, show our custom popup
    // with instructions on how to enable it
    else if (cameraState === 'denied') {
      // Set error type if not already set
      if (!this.cameraErrorType()) {
        this.cameraErrorType.set('denied');
      }
      this.showCameraPermissionPopup.set(true);
    }
    // If state is still unknown/prompt after check, try requesting anyway
    // This handles cases where navigator.permissions.query is not supported
    else {
      // Try to request permission - this will either grant it or show the popup
      await this.requestCameraPermission();
    }
  }

  // Enhanced permission check with debug info
  private async checkPermissions(): Promise<void> {
    try {
      // Check if permissions API is supported
      if ('permissions' in navigator && 'query' in navigator.permissions) {
        // Check camera permission
        const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
        this.permissions.update(prev => ({ ...prev, camera: cameraPermission.state }));

        // Check location permission
        const locationPermission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        this.permissions.update(prev => ({ ...prev, location: locationPermission.state }));

        // If camera permission is already granted, automatically start camera
        if (cameraPermission.state === 'granted') {
          this.onCameraPermissionGranted();
        }

        // Listen for permission changes
        cameraPermission.addEventListener('change', () => {
          this.permissions.update(prev => ({ ...prev, camera: cameraPermission.state }));

          if (cameraPermission.state === 'granted') {
            this.onCameraPermissionGranted();
          } else if (cameraPermission.state === 'denied') {
            // Show popup if permission is denied
            this.cameraErrorType.set('denied');
            this.showCameraPermissionPopup.set(true);
          }
        });
      } else {
        // Permissions API not supported - default to 'prompt' state
        // We'll try to request permission directly
        this.permissions.update(prev => ({
          ...prev,
          camera: 'prompt',
          location: 'prompt'
        }));
      }
    } catch (error) {
      // If permission query fails, default to 'prompt' state
      // This will trigger a permission request attempt
      this.permissions.update(prev => ({
        ...prev,
        camera: 'prompt',
        location: 'prompt'
      }));
    }
  }

  // Enhanced camera request with better error handling and fallbacks
  async requestCameraPermission(): Promise<void> {
    if (this.isRequestingPermission()) return;

    this.isRequestingPermission.set(true);

    try {
      // Laava-optimized camera constraints
      const constraints = {
        facingMode: 'environment',
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      };

      let stream: MediaStream | null = null;

      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: constraints });
        console.log(`Successfully found stream for resolution ${constraints.width.ideal}x${constraints.height.ideal}`);
      } catch (e) {
        console.error(`Failed to get stream for resolution ${constraints.width.ideal}x${constraints.height.ideal}: ${e}`);
        throw e;
      }

      if (!stream) {
        throw new Error('Failed to get camera stream');
      }

      this.permissions.update(prev => ({ ...prev, camera: 'granted' }));
      this.stream.set(stream);
      this.startCamera(stream);
      this.showCameraPermissionPopup.set(false);

    } catch (error: any) {
      console.error('Camera permission error:', error);

      // Handle specific error types with appropriate messaging
      if (error.name === 'NotAllowedError') {
        // User denied permission
        this.permissions.update(prev => ({ ...prev, camera: 'denied' }));
        this.cameraErrorType.set('denied');
        this.showCameraPermissionPopup.set(true);
      } else if (error.name === 'NotFoundError') {
        // No camera device found
        this.permissions.update(prev => ({ ...prev, camera: 'denied' }));
        this.cameraErrorType.set('not-found');
        this.showCameraPermissionPopup.set(true);
      } else if (error.name === 'NotReadableError' || error.name === 'AbortError') {
        // Camera is already in use in another tab/app
        this.permissions.update(prev => ({ ...prev, camera: 'denied' }));
        this.cameraErrorType.set('in-use');
        this.showCameraPermissionPopup.set(true);
      } else {
        // Unknown error
        this.permissions.update(prev => ({ ...prev, camera: 'denied' }));
        this.cameraErrorType.set('unknown');
        this.showCameraPermissionPopup.set(true);
      }
    } finally {
      this.isRequestingPermission.set(false);
    }
  }

  startCamera(stream: MediaStream): void {
    // Wait for view elements to be ready
    setTimeout(() => {
      if (this.videoElementRef && this.canvasElementRef) {
        const video = this.videoElementRef.nativeElement;
        const canvas = this.canvasElementRef.nativeElement;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        if (!ctx) {
          console.error('Failed to get canvas context');
          this.navigateToFingerprintError();
          return;
        }

        video.srcObject = stream;

        // Store references
        this.videoElement.set(video);
        this.canvasElement.set(canvas);
        this.context.set(ctx);

        // Initialize QR canvas if available (it might not be ready yet)
        if (this.qrCanvasRef) {
          const qrCanvas = this.qrCanvasRef.nativeElement;
          const qrCtx = qrCanvas.getContext('2d', { willReadFrequently: true });
          this.qrCanvas.set(qrCanvas);
          this.qrContext.set(qrCtx);
        }

        // Set up video event handlers
        video.onloadedmetadata = () => {
          // Clear the loading timeout
          clearTimeout(videoLoadTimeout);

          // Set canvas dimensions to match video resolution (optimized for mobile)
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 480;

          // Set QR overlay canvas dimensions if available
          if (this.qrCanvasRef) {
            const qrCanvas = this.qrCanvasRef.nativeElement;
            // Set canvas dimensions to match video display size, not resolution
            qrCanvas.width = video.offsetWidth || 320;
            qrCanvas.height = video.offsetHeight || 320;
          }

          // Start video playback
          video.play().then(() => {
            this.isCameraActive.set(true);
            this.isScanning.set(true);

            // Initialize QR canvas and start detection immediately
            this.initializeQRCanvas();
            // Draw the guide overlay right away (we still delay detection for camera stabilization)
            requestAnimationFrame(() => this.drawQRDetectionBox());
            // Give the camera time to stabilize (focus/exposure) before starting QR detection
            this.detectionStatus.set('Stabilizing camera...');
            if (this.detectionStartTimeoutId) {
              clearTimeout(this.detectionStartTimeoutId);
            }
            this.detectionStartTimeoutId = setTimeout(() => {
              // Only start if we are still scanning and camera is active
              if (this.isCameraActive() && this.isScanning() && !this.scanCompleted() && !this.isTagNotFound()) {
                this.detectionStatus.set('Scanning...');
                this.startQRCodeDetection();
              }
              this.detectionStartTimeoutId = null;
            }, 2000);

            // Check torch and zoom support after camera is active (non-blocking)
            setTimeout(() => {
              this.checkTorchSupport();

              // Apply auto-zoom and auto-focus for better QR detection
              this.applyAutoFocusAndZoom();

              // Start idle timeout monitoring (closes camera after inactivity)
              this.startIdleTimeout();
            }, 500);
          }).catch(error => {
            console.error('Video playback failed:', error);
            this.navigateToFingerprintError();
          });
        };

        video.onerror = (error) => {
          console.error('Video stream error:', error);
          this.navigateToFingerprintError();
        };

        // Add timeout for video loading
        const videoLoadTimeout = setTimeout(() => {
          if (!this.isCameraActive()) {
            console.error('Video failed to load within timeout');
            this.navigateToFingerprintError();
          }
        }, 5000); // Reduced from 10s to 5s for faster error detection

      } else {
        console.error('Video or canvas elements not found');
        this.navigateToFingerprintError();
      }
    }, 50); // Reduced delay for faster initialization
  }

  // Initialize QR canvas after camera becomes active
  private initializeQRCanvas(): void {
    if (!this.qrCanvasRef) return;

    const qrCanvas = this.qrCanvasRef.nativeElement;
    const qrCtx = qrCanvas.getContext('2d', { willReadFrequently: true });
    if (!qrCtx) return;

    // Always store refs (they may have been set earlier, but this ensures consistency)
    this.qrCanvas.set(qrCanvas);
    this.qrContext.set(qrCtx);

    // Set dimensions to match video display size, not resolution
    const video = this.videoElement();
    if (video) {
      qrCanvas.width = video.offsetWidth || 320;
      qrCanvas.height = video.offsetHeight || 320;
    }

    // Ensure the visual guide box exists as soon as the camera opens (don't wait for detection loop)
    if (!this.qrDetectionBox()) {
      this.initializeQRDetectionBox();
    }
  }

  // Initialize QR detection box in the center of the video
  private initializeQRDetectionBox(): void {
    const video = this.videoElement();
    if (!video) return;

    const videoWidth = video.offsetWidth || 320;
    const videoHeight = video.offsetHeight || 320;

    // Create a smaller, more focused box that's 40% of the video size (reduced from 60%)
    // This smaller box will help detect small QR codes more accurately
    const boxWidth = Math.min(videoWidth * 0.4, 150); // Max 150px width (reduced from 200px)
    const boxHeight = Math.min(videoHeight * 0.4, 150); // Max 150px height (reduced from 200px)

    const boxX = (videoWidth - boxWidth) / 2;
    const boxY = (videoHeight - boxHeight) / 2;

    this.qrDetectionBox.set({
      x: boxX,
      y: boxY,
      width: boxWidth,
      height: boxHeight
    });

    // Start with red color (no detection)
    this.qrBoxColor.set('red');
    this.showQRBox.set(true);
  }

  // Start continuous QR code detection with real-time scanning
  private startQRCodeDetection(): void {
    // Start QR detection timeout
    this.startQrDetectionTimeout();
    // Prevent multiple detection loops from running
    if (this.currentDetectionFrameId !== null) {
      cancelAnimationFrame(this.currentDetectionFrameId);
      this.currentDetectionFrameId = null;
    }

    let detectionCount = 0;
    let consecutiveFailures = 0;
    const maxConsecutiveFailures = 10;

    const detectQRCode = async () => {
      // Check if we should stop detection (but keep running during preparation to handle clearing)
      if (!this.isScanning() || this.scanCompleted() || this.isTagNotFound()) {
        if (this.currentDetectionFrameId) {
          cancelAnimationFrame(this.currentDetectionFrameId);
          this.currentDetectionFrameId = null;
        }
        return;
      }

      // Pause detection if tab is not visible to save resources
      if (!this.isTabVisible) {
        setTimeout(() => {
          this.currentDetectionFrameId = requestAnimationFrame(detectQRCode);
        }, 500); // Check every 500ms when tab is hidden
        return;
      }

      try {
        const video = this.videoElement();
        const canvas = this.canvasElement();
        const ctx = this.context();

        if (!video || !canvas || !ctx) {
          consecutiveFailures++;

          if (consecutiveFailures >= maxConsecutiveFailures) {
            console.error('Too many consecutive failures, stopping detection loop');
            return;
          }

          // Retry after a short delay
          setTimeout(() => {
            this.currentDetectionFrameId = requestAnimationFrame(detectQRCode);
          }, 100);
          return;
        }

        // Reset failure counter on successful element access
        consecutiveFailures = 0;

        // Draw current video frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Throttle getImageData calls to reduce performance warnings
        const now = Date.now();
        if (now - this.lastImageDataTime < this.imageDataThrottle) {
          // Skip detection this frame to throttle frequency
          this.currentDetectionFrameId = requestAnimationFrame(detectQRCode);
          return;
        }

        this.lastImageDataTime = now;

        // Get image data for QR detection - RESTRICTED TO CENTRAL BOX
        // We only scan the area inside the visual box to force users to center the QR
        let imageData: ImageData;
        const box = this.qrDetectionBox();

        if (box && video.videoWidth > 0 && video.offsetWidth > 0) {
          // Calculate scale factors between display size (what user sees) and actual video resolution (what we scan)
          const scaleX = video.videoWidth / video.offsetWidth;
          const scaleY = video.videoHeight / video.offsetHeight;

          // Map box coordinates to video resolution
          const scanX = Math.floor(box.x * scaleX);
          const scanY = Math.floor(box.y * scaleY);
          const scanWidth = Math.floor(box.width * scaleX);
          const scanHeight = Math.floor(box.height * scaleY);

          // Ensure we don't go out of bounds
          const finalX = Math.max(0, scanX);
          const finalY = Math.max(0, scanY);
          const finalWidth = Math.min(scanWidth, canvas.width - finalX);
          const finalHeight = Math.min(scanHeight, canvas.height - finalY);

          imageData = ctx.getImageData(finalX, finalY, finalWidth, finalHeight);
        } else {
          // Fallback to full frame if box not ready
          imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        }

        // Quick frame quality check - skip very dark/bright frames before expensive detection
        // Made less strict to avoid filtering out valid frames
        if (!this.isFrameGoodForDetection(imageData)) {
          this.currentDetectionFrameId = requestAnimationFrame(detectQRCode);
          return;
        }

        // Skip detection if we are already processing a capture
        // This prevents multiple triggers and "Processing QR code data" logs during burst capture
        if (this.isProcessing() || this.isUploading()) {
          this.currentDetectionFrameId = requestAnimationFrame(detectQRCode);
          return;
        }

        // Real QR code detection using ZXing (optimized with cached canvas)
        const qrDetected = await this.detectQRCodeInImage(imageData);


        // Track detection attempts
        detectionCount++;
        this.detectionAttempts.set(detectionCount);

        // Update detection status for user feedback (reduced frequency for performance)
        if (detectionCount % 50 === 0) {
          this.detectionStatus.set(`Scanning... (${detectionCount} attempts)`);
        }

        // Handle QR code detection with anti-false-positive mechanism
        if (qrDetected) {
          const now = Date.now();

          // Check if enough time has passed since last detection
          if (now - this.lastValidDetection > this.detectionDebounceTime) {
            this.consecutiveDetections = 1;
          } else {
            this.consecutiveDetections++;
          }

          this.lastValidDetection = now;

          // Change box color to green when QR is detected
          this.qrBoxColor.set('green');

          // Only proceed if we have enough consecutive detections
          if (this.consecutiveDetections >= this.requiredConsecutiveDetections) {

            // QR code is present - draw detection box and border
            this.drawQRDetectionBox();

            // Only trigger capture if not already processing and not triggered yet
            if (!this.isProcessing() && !this.isUploading()) {
              // If not already triggered, trigger it now
              if (!this.qrDetectionTriggered()) {
                this.qrDetectionTriggered.set(true);

                // Update activity to reset idle timer
                this.updateActivity();

                // Lock focus for sharp capture
                // Single-shot focus mode is safe and provides sharp images
                this.detectionStatus.set('QR Code Detected - Focusing...');

                // Lock focus for sharp capture
                try {
                  await this.lockFocusForCapture();
                  this.detectionStatus.set('Focus locked - Capturing...');
                } catch (error) {
                  console.warn('Focus lock failed, continuing with capture:', error);
                  this.detectionStatus.set('Capturing...');
                }

                this.scheduleCapture();
              }
            }
          }
        } else {
          // QR LOST - Handle misplaced QR during stabilization
          if (this.qrDetectionTriggered() && !this.isProcessing() && !this.isUploading()) {
            this.abortCapture();
            this.detectionStatus.set('QR Misplaced - Realign');
            // Don't reset qrDetectionTriggered immediately if you want to keep 'misplaced' state,
            // but simpler to just reset and let it restart.
          }

          // No QR code detected - reset consecutive count
          this.consecutiveDetections = 0;

          // Reset trigger state
          if (!this.isProcessing() && !this.isUploading()) {
            this.qrDetectionTriggered.set(false);
            this.qrBoxColor.set('red');
            // Redraw detection box with red color
            this.drawQRDetectionBox();
          }
        }

        // Continue detection loop with optimized frame rate for faster response
        // Use requestAnimationFrame for smooth detection with reduced frequency
        this.currentDetectionFrameId = requestAnimationFrame(detectQRCode);

      } catch (error) {
        consecutiveFailures++;
        console.error('QR detection error:', error);

        if (consecutiveFailures >= maxConsecutiveFailures) {
          console.error('Too many consecutive errors, stopping detection loop');
          return;
        }

        // Continue detection even if there's an error
        this.currentDetectionFrameId = requestAnimationFrame(detectQRCode);
      }
    };

    // Start the detection loop
    this.currentDetectionFrameId = requestAnimationFrame(detectQRCode);
  }


  // Quick frame quality check - skip very dark/bright frames before expensive detection
  private isFrameGoodForDetection(imageData: ImageData): boolean {
    // Sample random pixels for quick brightness check
    const sampleSize = Math.min(500, imageData.data.length / 4);
    let totalBrightness = 0;
    let count = 0;

    // Sample pixels at regular intervals
    const step = Math.floor((imageData.data.length / 4) / sampleSize);
    for (let i = 0; i < imageData.data.length && count < sampleSize; i += step * 4) {
      const r = imageData.data[i];
      const g = imageData.data[i + 1];
      const b = imageData.data[i + 2];
      totalBrightness += (0.299 * r + 0.587 * g + 0.114 * b);
      count++;
    }

    if (count === 0) return false;

    const avgBrightness = totalBrightness / count;

    // Only detect if brightness is reasonable (not too dark/bright)
    // Very lenient filter - only filter out completely black or white frames
    return avgBrightness > 10 && avgBrightness < 250;
  }

  // QR code detection using qr-scanner (replaces old ZXing detection)
  private async detectQRCodeInImage(imageData: ImageData): Promise<boolean> {
    try {
      // Create canvas from image data
      const canvas = document.createElement('canvas');
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return false;

      ctx.putImageData(imageData, 0, 0);

      // Use qr-scanner to detect QR code
      const qrScanSettings = {
        returnDetailedScanResult: true as const,
        alsoTryWithoutScanRegion: false,
      };

      try {
        const result = await QrScanner.scanImage(canvas, qrScanSettings);
        if (result && result.data) {
          this.processQRCodeData(result.data);
          return true;
        }
      } catch (_error) {
        // qr-scanner throws error when no code is found
      }

      return false;
    } catch (error) {
      console.error('QR detection error:', error);
      return false;
    }
  }

  // Helper method to extract QR code and tenant ID from any URL or compact code
  private extractQRCodeAndTenantFromURL(urlString: string): { qrCode: string | null; tenantId: string | null } | null {
    return extractQrCodeAndTenant(urlString);
  }

  // Process QR code data and extract information
  private processQRCodeData(qrData: string): void {
    console.log('Processing QR code data:', qrData);

    // Stop detection timeout since we found a QR
    this.stopQrDetectionTimeout();

    // Local Validation logic like in regenesis-auth
    const cleaned = String(qrData ?? '').toLowerCase();
    const isRegenesisQr = cleaned.includes('scan.regenesis.ag') || cleaned.includes('regenesis.ag') || cleaned.includes('scan.ag') || cleaned.includes('/s/');
    const isLaavaQr = cleaned.includes('laava.id');

    if (!isRegenesisQr && !isLaavaQr) {
      console.warn('[Scanner] Invalid QR pattern/domain detected (local check):', qrData);
      this.stopCamera();
      this.router.navigate(['/not-found'], { replaceUrl: true });
      return;
    }

    if (isRegenesisQr) {
      const extracted = this.extractQRCodeAndTenantFromURL(qrData);
      console.log('Extracted from regenesis URL:', extracted);

      if (extracted && extracted.qrCode && extracted.tenantId) {
        // Set QR code and tenant in state
        this.store.dispatch(new SetQRCode(extracted.qrCode));
        this.store.dispatch(new SetTenantId(extracted.tenantId));

        // Immediately update currentTenantId signal for immediate use
        this.currentTenantId.set(extracted.tenantId);

        console.log('Updated tenant ID to:', extracted.tenantId);
      } else {
        // Valid Regenesis domain but invalid format (e.g. missing tenant id)
        console.warn('[Scanner] Valid domain but invalid Regenesis format:', qrData);
        this.stopCamera();
        this.router.navigate(['/not-found'], { replaceUrl: true });
        return;
      }
    } else if (isLaavaQr) {
      // For Laava URLs, we allow them to proceed - the validation will happen during capture pipeline
      console.log('[Scanner] Laava QR detected, proceeding to capture pipeline');
    }
  }

  // Validate if detected QR code is actually valid (mobile-optimized)
  private isValidQRCode(data: string): boolean {
    if (!data || data.length === 0) {
      return false;
    }

    // Reject very short data (likely noise) - more lenient for mobile
    if (data.length < 2) {
      return false;
    }

    // Reject data that's just repeated characters (likely noise)
    if (data.length > 3 && data.split('').every((char: string) => char === data[0])) {
      return false;
    }

    // Check if it's a valid URL pattern
    if (data.startsWith('http://') || data.startsWith('https://')) {
      try {
        new URL(data);
        return true;
      } catch {
        // More lenient URL validation for mobile
        return data.includes('.') || data.includes('/');
      }
    }

    // More lenient QR code format validation for mobile devices
    const validPattern = /^[a-zA-Z0-9\-_\.\/\?\=\&\+\%\#\s]+$/;
    if (!validPattern.test(data)) {
      // Allow some additional characters that might appear in mobile QR codes
      const mobileValidPattern = /^[a-zA-Z0-9\-_\.\/\?\=\&\+\%\#\s\:]+$/;
      if (!mobileValidPattern.test(data)) {
        return false;
      }
    }

    return true;
  }




  // Convert RGBA image to grayscale for better QR detection
  private convertToGrayscale(imageData: ImageData): ImageData {
    const data = imageData.data;
    const grayscaleData = new Uint8ClampedArray(data.length);

    for (let i = 0; i < data.length; i += 4) {
      // Convert to grayscale using luminance formula
      const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);

      grayscaleData[i] = gray;     // Red
      grayscaleData[i + 1] = gray; // Green
      grayscaleData[i + 2] = gray; // Blue
      grayscaleData[i + 3] = data[i + 3]; // Alpha (keep original)
    }

    return new ImageData(grayscaleData, imageData.width, imageData.height);
  }



  // Capture progress tracking
  captureProgress = signal<number>(0);
  totalCaptureCount = signal<number>(this.REQUIRED_USABLE_FRAMES);

  // Automatic capture and processing with multiple variants
  private async autoCaptureAndProcess(): Promise<void> {
    if (this.isProcessing() || this.isUploading()) {
      return;
    }

    this.sessionCompleted = false;
    this.uploadResults.length = 0;
    this.cancelPendingUploads();
    this.isCapturing.set(true);
    this.isProcessing.set(true);
    this.detectionStatus.set('Processing QR code...');

    // Reset progress tracking
    this.captureProgress.set(0);

    try {
      for (let i = 0; i < this.REQUIRED_USABLE_FRAMES; i++) {
        if (this.sessionCompleted) break;

        // Update progress (1-based for UI)
        this.captureProgress.set(i + 1);

        const captured = await this.captureCurrentFrame();
        if (captured) {
          this.capturedImage.set(captured);
          this.startUploadForFrame(i, captured);
        }

        // Small delay between captures to allow for different frames
        if (i < this.REQUIRED_USABLE_FRAMES - 1 && !this.sessionCompleted) {
          await new Promise(resolve => setTimeout(resolve, 800));
        }
      }

      // If we've started all uploads but none finished with success yet, we stay in processing mode
      this.isProcessing.set(false);
      this.isUploading.set(true);
      this.detectionStatus.set('Validating QR code...');

    } catch (error) {
      console.error('Error in autoCaptureAndProcess:', error);
      await this.handleUploadError(error);
    } finally {
      this.isCapturing.set(false);
      // isUploading/isProcessing will be managed by upload completion or checkFinalize
    }
  }

  private startUploadForFrame(index: number, imageData: string) {
    if (this.sessionCompleted) return;

    const tenantId = this.currentTenantId();
    const qrCode = this.store.selectSnapshot(AppStateSelectors.getQRCode) || 'unknown';
    const sanitizedQRCode = qrCode.replaceAll(/[^a-zA-Z0-9]/g, '_');
    const now = new Date();
    const timestamp = now.toISOString().replaceAll(/[-:]/g, '').split('.')[0].replace('T', '-');
    const filename = `qr-code-${sanitizedQRCode}-${timestamp}-${index + 1}.jpg`;

    const file = this.base64ToFile(imageData, filename, 'image/jpeg');

    let location: { latitude: number; longitude: number; accuracy: number } | undefined;
    if (this.currentLocation()) {
      const currentLoc = this.currentLocation()!;
      location = {
        latitude: currentLoc.coords.latitude,
        longitude: currentLoc.coords.longitude,
        accuracy: currentLoc.coords.accuracy,
      };
    }

    const sub = this.imageUploadService.uploadImageAndScan(file, location, tenantId).subscribe({
      next: (response) => {
        if (this.sessionCompleted) return;

        const laavaData = response?.payload?.['laavaData'];
        const tagFound = laavaData && laavaData.message !== "Tag not found";
        const urlPositive = laavaData?.data?.custom_attributes?.url_positive;
        const validated = !!urlPositive;

        this.uploadResults.push({
          index,
          validated,
          tagFound: !!tagFound,
          laavaResponse: laavaData ?? null,
          imageKey: response?.imageKey,
          imageUrl: response?.imageUrl
        });

        if (tagFound && urlPositive) {
          this.sessionCompleted = true;
          this.finishAuthenticated(urlPositive);
          return;
        }

        this.checkFinalizeAfterAllResults();
      },
      error: (error) => {
        if (this.sessionCompleted) return;
        this.uploadResults.push({
          index,
          validated: false,
          tagFound: false,
          error: error?.message || String(error)
        });
        this.checkFinalizeAfterAllResults();
      }
    });

    this.uploadSubs.set(index, sub);
  }

  private finishAuthenticated(urlPositive: string) {
    this.cancelPendingUploads();
    this.stopCameraFeed();
    this.isScanning.set(false);
    this.isUploading.set(false);
    this.sessionCompleted = true;

    const qrCode = this.store.selectSnapshot(AppStateSelectors.getQRCode) || 'unknown';
    const tenantId = this.currentTenantId();

    this.store.dispatch(new SetPublicScanResults({
      createdAt: new Date().toISOString(),
      qrCode,
      tenantId,
      images: this.uploadResults.sort((a, b) => a.index - b.index)
    }));

    const extracted = this.extractQRCodeAndTenantFromURL(urlPositive);
    if (extracted && extracted.qrCode && extracted.tenantId) {
      this.store.dispatch(new SetQRCode(extracted.qrCode));
      this.store.dispatch(new SetTenantId(extracted.tenantId));
      this.store.dispatch(new SetAuthenticated(true));
      this.router.navigate([`/${extracted.qrCode}`], {
        queryParams: { 'x-tenant-id': extracted.tenantId }
      });
    } else {
      const resolvedTenant = resolveScanTenantId(qrCode, tenantId);
      this.router.navigate([`/${qrCode}`], {
        queryParams: { 'x-tenant-id': resolvedTenant }
      });
    }
  }

  private checkFinalizeAfterAllResults() {
    if (this.sessionCompleted) return;
    if (this.uploadResults.length < this.REQUIRED_USABLE_FRAMES) return;

    // All results in and none succeeded
    this.sessionCompleted = true;
    this.cancelPendingUploads();
    this.stopCameraFeed();
    this.isScanning.set(false);
    this.isUploading.set(false);
    this.isTagNotFound.set(true);
    this.store.dispatch(new SetTagNotFound(true));

    const qrCode = this.store.selectSnapshot(AppStateSelectors.getQRCode) || 'unknown';
    const tenantId = resolveScanTenantId(qrCode, this.currentTenantId());

    this.store.dispatch(new SetPublicScanResults({
      createdAt: new Date().toISOString(),
      qrCode,
      tenantId,
      images: this.uploadResults.sort((a, b) => a.index - b.index)
    }));

    this.router.navigate([`/${qrCode}`], {
      queryParams: { 'x-tenant-id': tenantId }
    });
  }

  private cancelPendingUploads() {
    this.uploadSubs.forEach(sub => sub.unsubscribe());
    this.uploadSubs.clear();
  }



  // Store image variants for potential retry
  private storeImageVariants(variants: ImageVariants): void {
    // Store variants in component state for potential retry with different quality
    this.imageVariants = {
      ...variants,
      timestamp: Date.now()
    };
  }

  // Capture current video frame with Laava-optimized QR detection and cropping
  private async captureCurrentFrame(): Promise<string> {
    const video = this.videoElement();
    const canvas = this.canvasElement();
    const ctx = this.context();

    if (!video || !canvas || !ctx) {
      throw new Error('Video elements not available');
    }

    // Wait for video to be ready and have valid frame data
    if (video.readyState < 2) {
      console.log('Waiting for video to be ready, current readyState:', video.readyState);
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Video not ready after timeout'));
        }, 2000);

        const onLoadedData = () => {
          clearTimeout(timeout);
          video.removeEventListener('loadeddata', onLoadedData);
          resolve();
        };

        video.addEventListener('loadeddata', onLoadedData);
        if (video.readyState >= 2) {
          clearTimeout(timeout);
          video.removeEventListener('loadeddata', onLoadedData);
          resolve();
        }
      });
    }

    // Validate video has valid dimensions
    if (!video.videoWidth || !video.videoHeight || video.videoWidth === 0 || video.videoHeight === 0) {
      throw new Error(`Invalid video dimensions: ${video.videoWidth}x${video.videoHeight}`);
    }

    console.log('Video ready for capture:', {
      readyState: video.readyState,
      dimensions: `${video.videoWidth}x${video.videoHeight}`
    });

    // Step 1: Capture full video frame to canvas
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Step 2: Detect QR code in center 900px using qr-scanner
    const qrDetectionCropSize = 900;
    const qrResult = await this.detectAndCropQRCode(canvas, qrDetectionCropSize);

    if (!qrResult) {
      throw new Error('QR code not detected or validation failed');
    }

    // Step 3: Crop to QR code with 1.4x multiplier
    return this.cropToQRCode(canvas, qrResult);
  }

  // Detect QR code in center area of image using qr-scanner
  private async detectAndCropQRCode(
    fullCanvas: HTMLCanvasElement,
    qrDetectionCropSize: number
  ): Promise<{ data: string; location: { x: number; y: number }[]; center: { x: number; y: number }; width: number; height: number; image: { width: number; height: number } } | null> {
    const canvasWidth = fullCanvas.width;
    const canvasHeight = fullCanvas.height;

    // Create a canvas for the center crop (900px)
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    const cropX = Math.max(0, centerX - qrDetectionCropSize / 2);
    const cropY = Math.max(0, centerY - qrDetectionCropSize / 2);
    const cropWidth = Math.min(qrDetectionCropSize, canvasWidth - cropX);
    const cropHeight = Math.min(qrDetectionCropSize, canvasHeight - cropY);

    // Create scaled canvas for QR detection (qr-scanner works better with smaller images)
    const scaledCanvas = document.createElement('canvas');
    const scale = Math.min(1, 640 / Math.max(cropWidth, cropHeight));
    scaledCanvas.width = cropWidth * scale;
    scaledCanvas.height = cropHeight * scale;
    const scaledCtx = scaledCanvas.getContext('2d', { willReadFrequently: true });
    if (!scaledCtx) return null;

    // Draw the center crop to scaled canvas
    scaledCtx.drawImage(
      fullCanvas,
      cropX, cropY, cropWidth, cropHeight,
      0, 0, scaledCanvas.width, scaledCanvas.height
    );

    // QR scanner settings
    const qrScanSettings = {
      returnDetailedScanResult: true as const,
      alsoTryWithoutScanRegion: false,
    };

    try {
      // Scan for QR code
      const result = await QrScanner.scanImage(scaledCanvas, qrScanSettings);

      if (!result || !result.data) {
        return null;
      }

      // Scale corner points back to original image coordinates
      const scaledCornerPoints = result.cornerPoints || [];
      const qrCodeCornerPoints = scaledCornerPoints.map((point) => ({
        x: (point.x / scale) + cropX,
        y: (point.y / scale) + cropY
      }));

      // Calculate QR code center and size
      const qrCodeCenterX = qrCodeCornerPoints.reduce((sum: number, p: { x: number }) => sum + p.x, 0) / qrCodeCornerPoints.length;
      const qrCodeCenterY = qrCodeCornerPoints.reduce((sum: number, p: { y: number }) => sum + p.y, 0) / qrCodeCornerPoints.length;

      // Calculate rotation-independent size (minimum enclosing square)
      const minX = Math.min(...qrCodeCornerPoints.map((p: { x: number }) => p.x));
      const maxX = Math.max(...qrCodeCornerPoints.map((p: { x: number }) => p.x));
      const minY = Math.min(...qrCodeCornerPoints.map((p: { y: number }) => p.y));
      const maxY = Math.max(...qrCodeCornerPoints.map((p: { y: number }) => p.y));
      const qrCodeSize = Math.max(maxX - minX, maxY - minY);

      // Step 2: Validate QR code
      // Must have side length >= 115px
      if (qrCodeSize < 115) {
        console.log(`QR code too small: ${qrCodeSize}px (minimum: 115px)`);
        return null;
      }

      // Center must be within 300px of image center
      const imageCenterX = canvasWidth / 2;
      const imageCenterY = canvasHeight / 2;
      const distanceFromCenter = Math.sqrt(
        Math.pow(qrCodeCenterX - imageCenterX, 2) + Math.pow(qrCodeCenterY - imageCenterY, 2)
      );

      if (distanceFromCenter > 300) {
        console.log(`QR code too far from center: ${distanceFromCenter}px (maximum: 300px)`);
        return null;
      }

      return {
        data: result.data,
        location: qrCodeCornerPoints,
        center: {
          x: qrCodeCenterX,
          y: qrCodeCenterY,
        },
        width: qrCodeSize,
        height: qrCodeSize,
        image: {
          width: canvasWidth,
          height: canvasHeight,
        },
      };
    } catch (error) {
      // "No QR code found" is a normal outcome while sampling frames; don't spam console as an error.
      const message = (error as any)?.message || '';
      if (!message.includes('No QR code found')) {
        console.error('QR code detection failed:', error);
      }
      return null;
    }
  }

  // Crop to QR code with 1.4x multiplier around minimum enclosing square
  private cropToQRCode(
    sourceCanvas: HTMLCanvasElement,
    qrCode: { location: { x: number; y: number }[]; center: { x: number; y: number }; width: number; height: number }
  ): string {
    const qrWidthMultiplier = 1.4;

    // Calculate minimum enclosing square from QR corner points
    const minX = Math.min(...qrCode.location.map((p) => p.x));
    const maxX = Math.max(...qrCode.location.map((p) => p.x));
    const minY = Math.min(...qrCode.location.map((p) => p.y));
    const maxY = Math.max(...qrCode.location.map((p) => p.y));

    const minimumEnclosingSquareSide = Math.max(maxX - minX, maxY - minY);
    const cropSize = minimumEnclosingSquareSide * qrWidthMultiplier;

    // Center crop on QR code
    const cropX = Math.max(0, Math.min(qrCode.center.x - cropSize / 2, sourceCanvas.width - cropSize));
    const cropY = Math.max(0, Math.min(qrCode.center.y - cropSize / 2, sourceCanvas.height - cropSize));
    const finalCropSize = Math.min(cropSize, Math.min(sourceCanvas.width - cropX, sourceCanvas.height - cropY));

    // Create output canvas (target size: 250-350px square)
    const targetSize = Math.min(350, Math.max(250, finalCropSize));
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = targetSize;
    outputCanvas.height = targetSize;
    const outputCtx = outputCanvas.getContext('2d', { willReadFrequently: true });
    if (!outputCtx) {
      throw new Error('Failed to get canvas context');
    }

    outputCtx.imageSmoothingEnabled = true;
    outputCtx.imageSmoothingQuality = 'high';

    // Draw cropped and resized QR code
    outputCtx.drawImage(
      sourceCanvas,
      cropX, cropY, finalCropSize, finalCropSize,
      0, 0, targetSize, targetSize
    );

    // Convert to JPEG (smaller file size)
    return outputCanvas.toDataURL('image/jpeg', 0.95);
  }

  // Helper method to capture with given coordinates
  private async captureWithCoordinates(
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    qrSourceX: number,
    qrSourceY: number,
    qrSourceWidth: number,
    qrSourceHeight: number
  ): Promise<string> {
    // Validate video is ready and has valid dimensions
    if (video.readyState < 2) {
      throw new Error(`Video not ready for capture. ReadyState: ${video.readyState}`);
    }

    if (!video.videoWidth || !video.videoHeight || video.videoWidth === 0 || video.videoHeight === 0) {
      throw new Error(`Invalid video dimensions: ${video.videoWidth}x${video.videoHeight}`);
    }

    console.log('Video dimensions:', { width: video.videoWidth, height: video.videoHeight });

    // Add generous padding around the QR code to prevent cutting edges
    // Use percentage-based padding that scales with QR size
    const paddingPercent = 0.5; // Increased to 50% of QR size to prevent cutting
    const minPadding = 80; // Increased minimum padding to 80 pixels for better margin
    const paddingX = Math.max(minPadding, Math.round(qrSourceWidth * paddingPercent));
    const paddingY = Math.max(minPadding, Math.round(qrSourceHeight * paddingPercent));

    const paddedSourceX = Math.max(0, qrSourceX - paddingX);
    const paddedSourceY = Math.max(0, qrSourceY - paddingY);
    const paddedSourceWidth = Math.min(video.videoWidth - paddedSourceX, qrSourceWidth + (paddingX * 2));
    const paddedSourceHeight = Math.min(video.videoHeight - paddedSourceY, qrSourceHeight + (paddingY * 2));

    console.log('QR area with padding:', {
      original: { x: qrSourceX, y: qrSourceY, w: qrSourceWidth, h: qrSourceHeight },
      padding: { x: paddingX, y: paddingY },
      padded: { x: paddedSourceX, y: paddedSourceY, w: paddedSourceWidth, h: paddedSourceHeight }
    });

    // Set canvas size to the QR code area size (with padding)
    // Ensure we maintain aspect ratio to prevent stretching
    let captureWidth = Math.round(paddedSourceWidth);
    let captureHeight = Math.round(paddedSourceHeight);

    // QR codes are typically square - ensure capture area maintains aspect ratio
    // Use the larger dimension to ensure we capture the full QR code
    const maxDimension = Math.max(captureWidth, captureHeight);

    // Make capture area square to prevent stretching (QR codes are square)
    // This ensures the captured image isn't distorted
    captureWidth = maxDimension;
    captureHeight = maxDimension;

    // Adjust source coordinates to maintain square aspect ratio
    const sourceMaxDimension = Math.max(paddedSourceWidth, paddedSourceHeight);
    const adjustedSourceWidth = sourceMaxDimension;
    const adjustedSourceHeight = sourceMaxDimension;

    // Center the square capture area
    const adjustedSourceX = paddedSourceX - Math.floor((adjustedSourceWidth - paddedSourceWidth) / 2);
    const adjustedSourceY = paddedSourceY - Math.floor((adjustedSourceHeight - paddedSourceHeight) / 2);

    // Ensure adjusted coordinates are within video bounds
    const finalSourceX = Math.max(0, Math.min(adjustedSourceX, video.videoWidth - adjustedSourceWidth));
    const finalSourceY = Math.max(0, Math.min(adjustedSourceY, video.videoHeight - adjustedSourceHeight));
    const finalSourceWidth = Math.min(adjustedSourceWidth, video.videoWidth - finalSourceX);
    const finalSourceHeight = Math.min(adjustedSourceHeight, video.videoHeight - finalSourceY);

    // Validate that we're not capturing the entire screen
    const totalVideoPixels = video.videoWidth * video.videoHeight;
    const capturePixels = captureWidth * captureHeight;
    const captureRatio = capturePixels / totalVideoPixels;

    // Upscale canvas for higher resolution capture - target ~230px QR codes (like Laava)
    // If base QR is ~115px, we need ~4x upscale to reach Laava's resolution
    const upscaleFactor = 4.0; // Increased to 4x to match Laava's QR code resolution (~230px)

    // Maintain aspect ratio when upscaling to prevent stretching
    const upscaledWidth = Math.round(captureWidth * upscaleFactor);
    const upscaledHeight = Math.round(captureHeight * upscaleFactor);

    canvas.width = upscaledWidth;
    canvas.height = upscaledHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Enable high-quality image smoothing for upscaling (produces sharper results)
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Validate coordinates are within video bounds
    if (paddedSourceX < 0 || paddedSourceY < 0 ||
      paddedSourceX + paddedSourceWidth > video.videoWidth ||
      paddedSourceY + paddedSourceHeight > video.videoHeight) {
      throw new Error(`Invalid capture coordinates: x=${paddedSourceX}, y=${paddedSourceY}, w=${paddedSourceWidth}, h=${paddedSourceHeight} (video: ${video.videoWidth}x${video.videoHeight})`);
    }

    // Validate capture area is valid
    if (paddedSourceWidth <= 0 || paddedSourceHeight <= 0) {
      throw new Error(`Invalid capture dimensions: ${paddedSourceWidth}x${paddedSourceHeight}`);
    }

    // Draw only the QR code area to the canvas at 4x resolution
    // Use square source area to prevent stretching (QR codes are square)
    ctx.drawImage(
      video,
      finalSourceX, finalSourceY, finalSourceWidth, finalSourceHeight,  // Source: square QR code area
      0, 0, canvas.width, canvas.height // Destination: square upscaled 4x canvas
    );

    // Verify aspect ratio is maintained (should be 1:1 for square QR codes)
    const canvasAspectRatio = canvas.width / canvas.height;
    const sourceAspectRatio = finalSourceWidth / finalSourceHeight;

    if (Math.abs(canvasAspectRatio - 1.0) > 0.01 || Math.abs(sourceAspectRatio - 1.0) > 0.01) {
      console.warn(`Aspect ratio not square: source=${sourceAspectRatio.toFixed(3)}, canvas=${canvasAspectRatio.toFixed(3)}`);
    }

    // Small delay to ensure canvas rendering is complete
    await new Promise(resolve => setTimeout(resolve, 10));

    // Verify we actually captured image data (not just blue/empty canvas)
    // Use larger sample area and check for any pixel variation (not just non-zero)
    const sampleSize = Math.min(50, Math.min(canvas.width, canvas.height));
    const testImageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
    const data = testImageData.data;

    // Check for pixel variation (not just non-zero) - dark backgrounds are valid
    let hasVariation = false;
    let minValue = 255;
    let maxValue = 0;

    for (let i = 0; i < data.length; i += 4) {
      // Skip alpha channel
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const brightness = (0.299 * r + 0.587 * g + 0.114 * b);

      minValue = Math.min(minValue, brightness);
      maxValue = Math.max(maxValue, brightness);

      // If we have any variation in pixels, we have valid image data
      if (maxValue - minValue > 5) {
        hasVariation = true;
        break;
      }
    }

    // Only fail if image is completely uniform (likely empty/invalid)
    // Dark backgrounds are valid, so we check for variation, not just non-zero
    if (!hasVariation && (maxValue - minValue) < 5) {
      console.warn('Warning: Captured image appears uniform, but continuing anyway');
      // Don't throw error - let it proceed, might still be valid
    }

    // Detect if background is dark (black) to prevent overexposure
    const isDarkBackground = this.detectDarkBackground(canvas);

    // Apply post-capture enhancement with dark background handling
    const enhancedCanvas = await this.applyPostCaptureEnhancement(canvas, isDarkBackground);

    // Convert to base64 with PNG for lossless quality (no compression artifacts)
    // File size will be larger but quality is critical for QR code processing
    const dataUrl = enhancedCanvas.toDataURL('image/png');

    // Validate data URL was created successfully
    if (!dataUrl || dataUrl === 'data:,') {
      throw new Error('Failed to create image data URL');
    }

    return dataUrl;
  }

  // Schedule auto-capture after stabilization
  private scheduleCapture(): void {
    if (this.captureAfterDetectionTimeoutId) {
      clearTimeout(this.captureAfterDetectionTimeoutId);
    }

    // Increased delay to ensure focus is fully locked for sharp images
    // Extra stabilization time after detection to improve capture sharpness
    this.detectionStatus.set('Stabilizing camera...');

    this.captureAfterDetectionTimeoutId = setTimeout(async () => {
      // Ensure video has rendered a frame before capturing
      const video = this.videoElement();
      if (video && video.readyState >= 2) {
        // Video is ready, wait a bit more for focus to fully settle
        await new Promise(resolve => setTimeout(resolve, 200)); // Additional wait for focus stability
        this.autoCaptureAndProcess();
      } else {
        // Wait a bit more for video to be ready
        console.log('Video not ready, waiting...', video?.readyState);
        await new Promise(resolve => setTimeout(resolve, 300));
        this.autoCaptureAndProcess();
      }
      this.captureAfterDetectionTimeoutId = null;
    }, 2000); // 2s stabilization after detection for clearer capture
  }

  // Abort pending capture if QR code alignment is lost
  private abortCapture(): void {
    if (this.captureAfterDetectionTimeoutId) {
      clearTimeout(this.captureAfterDetectionTimeoutId);
      this.captureAfterDetectionTimeoutId = null;
      console.log('Capture aborted - QR alignment lost');
    }
  }



  // Detect if background is dark (black) to prevent overexposure
  private detectDarkBackground(canvas: HTMLCanvasElement): boolean {
    try {
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return false;

      // Sample edges of image (where background usually is)
      const sampleSize = 50;
      const edgeWidth = Math.floor(canvas.width * 0.1); // Sample 10% from edges
      const edgeHeight = Math.floor(canvas.height * 0.1);

      // Sample top, bottom, left, right edges
      const samples: ImageData[] = [
        ctx.getImageData(0, 0, canvas.width, edgeHeight), // Top
        ctx.getImageData(0, canvas.height - edgeHeight, canvas.width, edgeHeight), // Bottom
        ctx.getImageData(0, 0, edgeWidth, canvas.height), // Left
        ctx.getImageData(canvas.width - edgeWidth, 0, edgeWidth, canvas.height) // Right
      ];

      let totalBrightness = 0;
      let pixelCount = 0;

      for (const sample of samples) {
        const data = sample.data;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
          totalBrightness += brightness;
          pixelCount++;
        }
      }

      if (pixelCount === 0) return false;

      const avgBrightness = totalBrightness / pixelCount;

      // Consider dark if average brightness is below 80 (dark background)
      return avgBrightness < 80;
    } catch (error) {
      console.warn('Dark background detection failed:', error);
      return false;
    }
  }

  // Apply post-capture enhancement for better API scanning - OPTIMIZED for clarity and overexposure prevention
  private async applyPostCaptureEnhancement(canvas: HTMLCanvasElement, isDarkBackground: boolean = false): Promise<HTMLCanvasElement> {
    try {
      // Enhanced processing for clearer images
      const enhancedCanvas = document.createElement('canvas');
      const enhancedCtx = enhancedCanvas.getContext('2d', { willReadFrequently: true })!;

      enhancedCanvas.width = canvas.width;
      enhancedCanvas.height = canvas.height;

      // Step 1: Apply contrast/brightness adjustment
      // For dark backgrounds, reduce brightness to prevent overexposure (FFFFFF white)
      if (isDarkBackground) {
        // Dark background: reduce brightness significantly to preserve invisiprint dots
        enhancedCtx.filter = 'contrast(1.2) brightness(0.85)'; // Reduced brightness for dark backgrounds
        console.log('Dark background detected - reducing brightness to prevent overexposure');
      } else {
        // Normal background: standard enhancement
        enhancedCtx.filter = 'contrast(1.15) brightness(1.05)';
      }
      enhancedCtx.drawImage(canvas, 0, 0);

      // Step 2: Apply light unsharp mask for sharpening (improves clarity)
      const sharpened = this.applyUnsharpMask(enhancedCanvas, 1.2, 0.8);

      return sharpened;
    } catch (error) {
      console.warn('Post-capture enhancement failed, using original:', error);
      return canvas; // Fallback to original if enhancement fails
    }
  }

  // Apply unsharp mask sharpening filter
  // amount: sharpening strength (1.0 = subtle, 2.0 = strong)
  // radius: blur radius (0.5 = fine detail, 2.0 = broader)
  private applyUnsharpMask(canvas: HTMLCanvasElement, amount: number = 1.5, radius: number = 1.0): HTMLCanvasElement {
    try {
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return canvas;

      const width = canvas.width;
      const height = canvas.height;

      // Get original image data
      const original = ctx.getImageData(0, 0, width, height);
      const originalData = original.data;

      // Create blurred version (simple box blur for performance)
      const blurred = ctx.createImageData(width, height);
      const blurredData = blurred.data;

      // Simple 3x3 box blur
      const blurRadius = Math.max(1, Math.round(radius));
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          let r = 0, g = 0, b = 0, count = 0;

          for (let dy = -blurRadius; dy <= blurRadius; dy++) {
            for (let dx = -blurRadius; dx <= blurRadius; dx++) {
              const nx = Math.min(Math.max(x + dx, 0), width - 1);
              const ny = Math.min(Math.max(y + dy, 0), height - 1);
              const i = (ny * width + nx) * 4;

              r += originalData[i];
              g += originalData[i + 1];
              b += originalData[i + 2];
              count++;
            }
          }

          const i = (y * width + x) * 4;
          blurredData[i] = r / count;
          blurredData[i + 1] = g / count;
          blurredData[i + 2] = b / count;
          blurredData[i + 3] = originalData[i + 3];
        }
      }

      // Apply unsharp mask: original + amount * (original - blurred)
      const sharpened = ctx.createImageData(width, height);
      const sharpenedData = sharpened.data;

      for (let i = 0; i < originalData.length; i += 4) {
        const diff_r = originalData[i] - blurredData[i];
        const diff_g = originalData[i + 1] - blurredData[i + 1];
        const diff_b = originalData[i + 2] - blurredData[i + 2];

        sharpenedData[i] = Math.min(255, Math.max(0, originalData[i] + diff_r * amount));
        sharpenedData[i + 1] = Math.min(255, Math.max(0, originalData[i + 1] + diff_g * amount));
        sharpenedData[i + 2] = Math.min(255, Math.max(0, originalData[i + 2] + diff_b * amount));
        sharpenedData[i + 3] = originalData[i + 3];
      }

      // Put sharpened data back
      ctx.putImageData(sharpened, 0, 0);

      return canvas;
    } catch (error) {
      console.warn('Unsharp mask failed:', error);
      return canvas;
    }
  }


  // Upload and process image with Laava
  private async uploadAndProcessImage(imageData: string): Promise<void> {
    try {
      // Create Pica-optimized version for better scanning results
      const picaOptimizedImageData = await this.createPicaOptimizedImage(imageData);

      // Get QR code for filename
      const qrCode = this.store.selectSnapshot(AppStateSelectors.getQRCode) || 'unknown';
      const sanitizedQRCode = qrCode.replace(/[^a-zA-Z0-9]/g, '_'); // Sanitize for filename

      // Add timestamp to filename (format: YYYYMMDD-HHMMSS)
      const now = new Date();
      const timestamp = now.toISOString().replace(/[-:]/g, '').split('.')[0].replace('T', '-'); // Format: 20231205-134530

      const filename = `qr-code-${sanitizedQRCode}-${timestamp}.jpg`;

      // Convert Pica-optimized base64 to JPEG file
      const file = this.base64ToFile(picaOptimizedImageData, filename, 'image/jpeg');


      // Prepare location data
      let location;
      if (this.currentLocation()) {
        const currentLocation = this.currentLocation()!;
        location = {
          latitude: currentLocation.coords.latitude,
          longitude: currentLocation.coords.longitude,
          accuracy: currentLocation.coords.accuracy,
        };
      }

      // Upload to S3 and process with Laava
      const tenantId = this.currentTenantId();
      console.log('Uploading with tenant ID:', tenantId);
      const response = await this.imageUploadService.uploadImageAndScan(file, location, tenantId).toPromise();

      // Check if response is successful (either status: "success" or success: true)
      const isSuccess = response && (response.status === 'success' || response.success === true);

      if (isSuccess) {
        // Reset retry count on success
        this.uploadRetryCount.set(0);

        // Stop scanning loop to prevent additional uploads, but keep camera active
        this.stopScanningLoop();

        // Check Laava response for tag detection
        const laavaData = response.payload?.['laavaData'];
        const isTagFound = laavaData && laavaData.message !== "Tag not found";

        if (isTagFound) {
          // Tag found - must have url_positive to be considered truly authenticated
          const urlPositive = laavaData?.data?.custom_attributes?.url_positive;

          if (urlPositive) {
            // Use the existing helper method to extract QR code and tenant ID
            const extracted = this.extractQRCodeAndTenantFromURL(urlPositive);

            if (extracted && extracted.qrCode && extracted.tenantId) {
              // Set QR code and tenant ID in store for authenticity component
              this.store.dispatch(new SetQRCode(extracted.qrCode));
              this.store.dispatch(new SetTenantId(extracted.tenantId));

              // Set authenticated state in store so authenticity component shows authenticated view
              this.store.dispatch(new SetAuthenticated(true));

              // Show success message briefly before redirect
              this.isProcessing.set(false);
              this.isUploading.set(false);
              this.isTagNotFound.set(false);
              this.store.dispatch(new SetTagNotFound(false));
              // Redirect to the extracted URL
              this.router.navigate([`/${extracted.qrCode}`], {
                queryParams: {
                  'x-tenant-id': extracted.tenantId
                }
              });

              return; // Exit early since we successfully processed the URL
            }
          }

          // If urlPositive is not present or parsing failed, consider it as tag not found

          // Set tag not found state
          this.isTagNotFound.set(true);
          this.store.dispatch(new SetTagNotFound(true));

          // Stop all scanning loops immediately
          this.stopScanningLoop();
          this.stopQRDetectionLoop();
          this.isScanning.set(false);

          // Get QR code from state for redirect
          const qrCode = this.qrCode || '00000000000001';

          this.router.navigate([`/${qrCode}`], {
            queryParams: {
              'x-tenant-id': resolveScanTenantId(qrCode, this.currentTenantId())
            }
          });

          return; // Exit early since we're treating this as tag not found

        } else {
          // Tag not found - get QR code and tenant ID from store state
          // For tag not found, we use the QR code that was detected during scanning
          const qrCode = this.store.selectSnapshot(AppStateSelectors.getQRCode) || '00000000000001';
          const tenantId = resolveScanTenantId(qrCode, this.currentTenantId());

          // Set QR code and tenant ID in store for tag not found (if not already set)
          if (qrCode !== '00000000000001') {
            this.store.dispatch(new SetQRCode(qrCode));
            this.store.dispatch(new SetTenantId(tenantId));
          }

          // Set authentication state to false for tag not found
          this.store.dispatch(new SetAuthenticated(false));

          // Immediately stop scanning and redirect
          this.isProcessing.set(false);
          this.isUploading.set(false);
          this.isTagNotFound.set(true);

          // Stop all scanning loops immediately
          this.stopScanningLoop();
          this.stopQRDetectionLoop();
          this.isScanning.set(false);

          // Dispatch action to set tag not found state in the store
          this.store.dispatch(new SetTagNotFound(true));

          // Redirect immediately using Angular Router
          this.router.navigate([`/${qrCode}`], {
            queryParams: {
              'x-tenant-id': tenantId
            }
          });
        }

      } else {
        throw new Error(response?.error || response?.message || 'Upload or processing failed');
      }

    } catch (error: any) {
      throw error; // Re-throw to be handled by handleUploadError
    }
  }

  // Handle upload errors with retry logic and quality fallback
  private async handleUploadError(error: any): Promise<void> {
    const currentRetryCount = this.uploadRetryCount();

    if (currentRetryCount < this.maxUploadRetries) {
      // Increment retry count and set retry state
      this.uploadRetryCount.update(prev => prev + 1);
      this.isRetrying.set(true);

      // Wait a bit before retrying
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Retry the upload with potentially different image quality
      try {
        const imageData = await this.getRetryImageData(currentRetryCount);
        if (imageData) {
          await this.uploadAndProcessImage(imageData);
          // If retry succeeds, we'll stop scanning in uploadAndProcessImage
          this.isRetrying.set(false);
        }
      } catch (retryError) {
        // If retry also fails, try again or show error
        if (this.uploadRetryCount() >= this.maxUploadRetries) {
          this.isRetrying.set(false);
          this.navigateToFingerprintError();
        } else {
          await this.handleUploadError(retryError);
        }
      }
    } else {
      // Max retries reached, navigate to fingerprint error page
      this.isRetrying.set(false);
      this.navigateToFingerprintError();
    }
  }

  // Get appropriate image data for retry based on attempt number
  private async getRetryImageData(retryCount: number): Promise<string | null> {
    try {
      // Check if we have stored variants
      if (this.imageVariants) {
        // Use different JPEG variants based on retry attempt
        switch (retryCount) {
          case 1:
            // First retry: try medium quality JPEG (faster upload)
            return this.imageVariants.mediumQuality;
          default:
            // Fallback to original high quality JPEG
            return this.imageVariants.highQuality;
        }
      }

      // Fallback to stored captured image
      return this.capturedImage();
    } catch (error) {
      console.warn('Error getting retry image data:', error);
      return this.capturedImage();
    }
  }
  // Mobile-optimized enhancement for better QR detection on mobile devices
  private async applyMobileOptimizedEnhancement(imageData: ImageData): Promise<ImageData> {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

      canvas.width = imageData.width;
      canvas.height = imageData.height;

      // Put original image data
      ctx.putImageData(imageData, 0, 0);

      // Apply enhanced mobile-optimized filters for small QR codes
      // Higher contrast, brightness, and sharpness for better small code detection
      // Reduced brightness boost to prevent overexposure (was 1.5)
      // Reduced contrast to prevent washing out details (was 2.2)
      ctx.filter = 'contrast(1.2) brightness(1.1) saturate(1.1) grayscale(0.1)';

      // Draw enhanced image to new canvas
      const enhancedCanvas = document.createElement('canvas');
      const enhancedCtx = enhancedCanvas.getContext('2d', { willReadFrequently: true })!;
      enhancedCanvas.width = imageData.width;
      enhancedCanvas.height = imageData.height;

      enhancedCtx.drawImage(canvas, 0, 0);

      // Get enhanced image data
      const enhancedImageData = enhancedCtx.getImageData(0, 0, enhancedCanvas.width, enhancedCanvas.height);

      return enhancedImageData;
    } catch (error) {
      console.error('Mobile enhancement error:', error);
      throw error;
    }
  }

  // Small QR code enhancement - ultra-aggressive processing for tiny codes
  private async applySmallQRCodeEnhancement(imageData: ImageData): Promise<ImageData> {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

      canvas.width = imageData.width;
      canvas.height = imageData.height;

      // Put original image data
      ctx.putImageData(imageData, 0, 0);

      // Apply ultra-aggressive enhancement specifically for very small QR codes
      // Maximum contrast and brightness to make tiny codes more visible
      ctx.filter = 'contrast(3.0) brightness(1.8) saturate(1.6) grayscale(0.3)';

      // Draw enhanced image to new canvas
      const enhancedCanvas = document.createElement('canvas');
      const enhancedCtx = enhancedCanvas.getContext('2d', { willReadFrequently: true })!;
      enhancedCanvas.width = imageData.width;
      enhancedCanvas.height = imageData.height;

      enhancedCtx.drawImage(canvas, 0, 0);

      // Apply additional sharpening for small QR codes
      enhancedCtx.filter = 'blur(0.5px)';
      enhancedCtx.drawImage(enhancedCanvas, 0, 0);

      // Get enhanced image data
      const enhancedImageData = enhancedCtx.getImageData(0, 0, enhancedCanvas.width, enhancedCanvas.height);

      return enhancedImageData;
    } catch (error) {
      console.error('Small QR code enhancement error:', error);
      throw error;
    }
  }

  // Scale image for small QR code detection
  private async scaleImageForSmallQR(imageData: ImageData): Promise<ImageData> {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

      // Scale up the image for better small QR code detection
      const scaleFactor = 2; // Double the size
      canvas.width = imageData.width * scaleFactor;
      canvas.height = imageData.height * scaleFactor;

      // Put original image data
      ctx.putImageData(imageData, 0, 0);

      // Apply enhancement after scaling
      ctx.filter = 'contrast(2.5) brightness(1.6) saturate(1.4)';

      // Draw enhanced image to new canvas
      const enhancedCanvas = document.createElement('canvas');
      const enhancedCtx = enhancedCanvas.getContext('2d', { willReadFrequently: true })!;
      enhancedCanvas.width = canvas.width;
      enhancedCanvas.height = canvas.height;

      enhancedCtx.drawImage(canvas, 0, 0);

      // Get enhanced image data
      const enhancedImageData = enhancedCtx.getImageData(0, 0, enhancedCanvas.width, enhancedCanvas.height);

      return enhancedImageData;
    } catch (error) {
      console.error('Image scaling error:', error);
      throw error;
    }
  }

  // Ultra-aggressive enhancement for very small QR codes
  private async applyUltraAggressiveEnhancement(imageData: ImageData): Promise<ImageData> {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

      canvas.width = imageData.width;
      canvas.height = imageData.height;

      // Put original image data
      ctx.putImageData(imageData, 0, 0);

      // Apply maximum enhancement for very small QR codes
      // Extreme contrast and brightness to make tiny codes visible
      ctx.filter = 'contrast(4.0) brightness(2.0) saturate(2.0) grayscale(0.5)';

      // Draw enhanced image to new canvas
      const enhancedCanvas = document.createElement('canvas');
      const enhancedCtx = enhancedCanvas.getContext('2d', { willReadFrequently: true })!;
      enhancedCanvas.width = imageData.width;
      enhancedCanvas.height = imageData.height;

      enhancedCtx.drawImage(canvas, 0, 0);

      // Apply multiple sharpening passes for very small QR codes
      enhancedCtx.filter = 'blur(0.3px)';
      enhancedCtx.drawImage(enhancedCanvas, 0, 0);

      // Get enhanced image data
      const enhancedImageData = enhancedCtx.getImageData(0, 0, enhancedCanvas.width, enhancedCanvas.height);

      return enhancedImageData;
    } catch (error) {
      console.error('Ultra-aggressive enhancement error:', error);
      throw error;
    }
  }

  // Apply noise reduction to improve QR detection
  private applyNoiseReduction(imageData: ImageData): ImageData {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    const newData = new Uint8ClampedArray(data.length);

    // Simple 3x3 median filter for noise reduction
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;

        // Collect neighboring pixel values
        const neighbors = [];
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const nIdx = ((y + dy) * width + (x + dx)) * 4;
            neighbors.push(data[nIdx]); // Use red channel for grayscale
          }
        }

        // Sort and get median
        neighbors.sort((a, b) => a - b);
        const median = neighbors[4]; // Middle value

        // Apply median to all channels
        newData[idx] = median;     // Red
        newData[idx + 1] = median; // Green
        newData[idx + 2] = median; // Blue
        newData[idx + 3] = data[idx + 3]; // Alpha
      }
    }

    // Copy border pixels
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (y === 0 || y === height - 1 || x === 0 || x === width - 1) {
          const idx = (y * width + x) * 4;
          newData[idx] = data[idx];
          newData[idx + 1] = data[idx + 1];
          newData[idx + 2] = data[idx + 2];
          newData[idx + 3] = data[idx + 3];
        }
      }
    }

    return new ImageData(newData, width, height);
  }

  // Apply edge enhancement for blurry mobile images
  private applyEdgeEnhancement(imageData: ImageData): ImageData {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    const newData = new Uint8ClampedArray(data.length);

    // Simple edge enhancement using Laplacian filter
    const laplacianKernel = [
      [0, -1, 0],
      [-1, 5, -1],
      [0, -1, 0]
    ];

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;

        let enhancedValue = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const nIdx = ((y + dy) * width + (x + dx)) * 4;
            const weight = laplacianKernel[dy + 1][dx + 1];
            enhancedValue += data[nIdx] * weight;
          }
        }

        // Clamp values to valid range
        enhancedValue = Math.max(0, Math.min(255, enhancedValue));

        newData[idx] = enhancedValue;     // Red
        newData[idx + 1] = enhancedValue; // Green
        newData[idx + 2] = enhancedValue; // Blue
        newData[idx + 3] = data[idx + 3]; // Alpha
      }
    }

    // Copy border pixels
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (y === 0 || y === height - 1 || x === 0 || x === width - 1) {
          const idx = (y * width + x) * 4;
          newData[idx] = data[idx];
          newData[idx + 1] = data[idx + 1];
          newData[idx + 2] = data[idx + 2];
          newData[idx + 3] = data[idx + 3];
        }
      }
    }

    return new ImageData(newData, width, height);
  }

  // Apply adaptive thresholding for varying lighting conditions
  private applyAdaptiveThresholding(imageData: ImageData): ImageData {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    const newData = new Uint8ClampedArray(data.length);

    // Calculate local mean for adaptive thresholding
    const windowSize = 15; // Local window size
    const halfWindow = Math.floor(windowSize / 2);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;

        // Calculate local mean
        let sum = 0;
        let count = 0;

        for (let dy = -halfWindow; dy <= halfWindow; dy++) {
          for (let dx = -halfWindow; dx <= halfWindow; dx++) {
            const ny = y + dy;
            const nx = x + dx;

            if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
              const nIdx = (ny * width + nx) * 4;
              sum += data[nIdx];
              count++;
            }
          }
        }

        const localMean = sum / count;
        const threshold = localMean * 0.9; // Adaptive threshold

        // Apply threshold
        const grayValue = data[idx];
        const binaryValue = grayValue > threshold ? 255 : 0;

        newData[idx] = binaryValue;     // Red
        newData[idx + 1] = binaryValue; // Green
        newData[idx + 2] = binaryValue; // Blue
        newData[idx + 3] = data[idx + 3]; // Alpha
      }
    }

    return new ImageData(newData, width, height);
  }

  // Simple canvas enhancement for QR code detection (legacy method)
  private async applySimpleCanvasEnhancement(imageData: ImageData): Promise<ImageData> {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

      canvas.width = imageData.width;
      canvas.height = imageData.height;

      // Put original image data
      ctx.putImageData(imageData, 0, 0);

      // Apply simple enhancement filters
      ctx.filter = 'contrast(1.3) brightness(1.05) saturate(0.9)';

      // Draw enhanced image to new canvas
      const enhancedCanvas = document.createElement('canvas');
      const enhancedCtx = enhancedCanvas.getContext('2d', { willReadFrequently: true })!;
      enhancedCanvas.width = imageData.width;
      enhancedCanvas.height = imageData.height;

      enhancedCtx.drawImage(canvas, 0, 0);

      // Get enhanced image data
      const enhancedImageData = enhancedCtx.getImageData(0, 0, enhancedCanvas.width, enhancedCanvas.height);

      // Apply grayscale conversion for better QR detection
      const grayscaleData = this.convertToGrayscale(enhancedImageData);

      return grayscaleData;
    } catch (error) {
      console.error('Simple canvas enhancement error:', error);
      throw error;
    }
  }

  // Pica-based high-quality image optimization for noisy/blurry images
  private async createPicaOptimizedImage(originalImageData: string): Promise<string> {
    try {
      // Create source canvas from base64 image
      const sourceCanvas = document.createElement('canvas');
      const sourceCtx = sourceCanvas.getContext('2d', { willReadFrequently: true })!;
      const img = new Image();

      return new Promise((resolve, reject) => {
        img.onload = async () => {
          try {
            // Set source canvas size
            sourceCanvas.width = img.width;
            sourceCanvas.height = img.height;

            // Draw original image to source canvas
            sourceCtx.drawImage(img, 0, 0);

            // Create destination canvas with optimal size for QR detection
            const destCanvas = document.createElement('canvas');

            // Calculate optimal dimensions - crop instead of resize to avoid compression
            const optimalSize = this.calculateOptimalSize(img.width, img.height);
            destCanvas.width = optimalSize.width;
            destCanvas.height = optimalSize.height;

            // Crop and scale instead of compressing - maintains QR code clarity
            const destCtx = destCanvas.getContext('2d', { willReadFrequently: true })!;
            destCtx.imageSmoothingEnabled = true;
            destCtx.imageSmoothingQuality = 'high';

            // If we need to crop, draw the cropped portion
            if (optimalSize.cropX !== undefined && optimalSize.cropY !== undefined &&
              optimalSize.cropWidth !== undefined && optimalSize.cropHeight !== undefined) {
              // Crop top/bottom to maintain aspect ratio
              destCtx.drawImage(
                sourceCanvas,
                optimalSize.cropX, optimalSize.cropY, optimalSize.cropWidth, optimalSize.cropHeight,
                0, 0, optimalSize.width, optimalSize.height
              );
            } else {
              // No cropping needed, just scale if necessary
              if (img.width !== optimalSize.width || img.height !== optimalSize.height) {
                // Use Pica for high-quality scaling (not compression)
                await this.pica.resize(sourceCanvas, destCanvas, {
                  quality: 3, // Highest quality
                  unsharpAmount: 80, // Unsharp masking for edge enhancement
                  unsharpRadius: 0.6, // Unsharp radius
                  unsharpThreshold: 2 // Unsharp threshold
                });
              } else {
                // Same size, just copy
                destCtx.drawImage(sourceCanvas, 0, 0);
              }
            }

            // Apply additional Pica-based enhancement
            const enhancedCanvas = await this.applyPicaEnhancement(destCanvas);

            // Convert to JPEG with very high quality for optimal QR detection (0.98)
            const optimizedDataUrl = enhancedCanvas.toDataURL('image/jpeg', 0.98);

            resolve(optimizedDataUrl);

          } catch (error) {
            console.error('Pica optimization error:', error);
            reject(error);
          }
        };

        img.onerror = () => reject(new Error('Failed to load image for Pica optimization'));
        img.src = originalImageData;
      });
    } catch (error) {
      console.warn('Pica optimization failed, using original:', error);
      return originalImageData;
    }
  }

  // Calculate optimal size for QR code detection with crop coordinates
  private calculateOptimalSize(originalWidth: number, originalHeight: number): {
    width: number;
    height: number;
    cropX?: number;
    cropY?: number;
    cropWidth?: number;
    cropHeight?: number;
  } {
    // Target size that balances quality and performance
    const targetSize = 1536; // Target width for QR code detection

    // If original is smaller than target, use original size
    if (originalWidth < targetSize && originalHeight < targetSize) {
      return { width: originalWidth, height: originalHeight };
    }

    // Calculate crop to maintain aspect ratio - crop top/bottom to match target width
    // This prevents compression/distortion of the QR code
    const aspectRatio = originalWidth / originalHeight;
    let width: number, height: number;
    const cropX = 0;
    let cropY = 0;
    let cropWidth = originalWidth;
    let cropHeight = originalHeight;

    if (originalWidth >= targetSize) {
      // Use target width, crop height to maintain aspect ratio
      width = targetSize;
      height = Math.round(targetSize / aspectRatio);

      // If calculated height is less than original, crop top/bottom
      if (height < originalHeight) {
        cropHeight = height;
        cropY = Math.round((originalHeight - height) / 2); // Center the crop
      }
    } else {
      // Original width is smaller, scale up but maintain aspect ratio
      width = originalWidth;
      height = originalHeight;
    }

    // Ensure dimensions are even numbers (better for processing)
    width = width % 2 === 0 ? width : width - 1;
    height = height % 2 === 0 ? height : height - 1;
    cropWidth = cropWidth % 2 === 0 ? cropWidth : cropWidth - 1;
    cropHeight = cropHeight % 2 === 0 ? cropHeight : cropHeight - 1;

    return {
      width,
      height,
      cropX,
      cropY,
      cropWidth,
      cropHeight
    };
  }

  // Apply Pica-based enhancement techniques
  private async applyPicaEnhancement(canvas: HTMLCanvasElement): Promise<HTMLCanvasElement> {
    try {
      // Create enhancement canvas
      const enhancedCanvas = document.createElement('canvas');
      enhancedCanvas.width = canvas.width;
      enhancedCanvas.height = canvas.height;

      // Apply subtle enhancement using Pica's unsharp masking
      await this.pica.resize(canvas, enhancedCanvas, {
        quality: 2, // High quality
        unsharpAmount: 40, // Subtle sharpening
        unsharpRadius: 0.4,
        unsharpThreshold: 1
      });

      return enhancedCanvas;
    } catch (error) {
      console.warn('Pica enhancement failed, returning original:', error);
      return canvas;
    }
  }

  // Navigate to QR code error page
  private navigateToFingerprintError(): void {
    this.router.navigate(['/qrcode-error']);
  }



  // Stop scanning loop without stopping camera
  private stopScanningLoop(): void {
    this.isScanning.set(false);
    // Clear any remaining QR borders
    this.clearQRCodeBorder();
    // Also stop the QR detection loop
    this.stopQRDetectionLoop();
  }

  // Stop QR detection loop
  private stopQRDetectionLoop(): void {
    this.stopQrDetectionTimeout();
    if (this.detectionStartTimeoutId) {
      clearTimeout(this.detectionStartTimeoutId);
      this.detectionStartTimeoutId = null;
    }
    if (this.captureAfterDetectionTimeoutId) {
      clearTimeout(this.captureAfterDetectionTimeoutId);
      this.captureAfterDetectionTimeoutId = null;
    }
    if (this.currentDetectionFrameId !== null) {
      cancelAnimationFrame(this.currentDetectionFrameId);
      this.currentDetectionFrameId = null;
    }
  }

  // Restart QR detection loop (for manual recovery)
  private restartQRDetectionLoop(): void {
    // Don't restart if tag is not found
    if (this.isTagNotFound()) {
      return;
    }

    this.stopQRDetectionLoop();

    // Reset detection state
    this.consecutiveDetections = 0;
    this.lastValidDetection = 0;
    this.qrDetectionTriggered.set(false);

    // Clear QR data - no longer needed since we use state

    // Only reset detection box to red if we haven't detected a QR code yet
    // If QR was already detected, keep it green
    if (!this.qrDetectionTriggered()) {
      this.qrBoxColor.set('red');
    }

    // Restart with a small delay
    setTimeout(() => {
      this.isScanning.set(true);
      this.startQRCodeDetection();
    }, 50);
  }

  // Draw QR detection box on overlay canvas
  private drawQRDetectionBox(): void {
    // Ensure QR canvas is initialized
    if (!this.qrCanvas() && this.qrCanvasRef) {
      this.initializeQRCanvas();
    }

    const qrCanvas = this.qrCanvas();
    const qrCtx = this.qrContext();
    const qrDetectionBox = this.qrDetectionBox();

    if (!qrCanvas || !qrCtx || !qrDetectionBox) {
      return;
    }

    // Clear previous drawings on overlay canvas
    qrCtx.clearRect(0, 0, qrCanvas.width, qrCanvas.height);

    // Draw the QR detection box
    this.drawDetectionBox(qrCtx, qrDetectionBox);
  }

  // Draw the detection box (red when no detection, green when detected)
  private drawDetectionBox(ctx: CanvasRenderingContext2D, box: { x: number; y: number; width: number; height: number }): void {
    const color = this.qrBoxColor();
    const opacity = this.qrBoxOpacity();

    // Draw outer dotted grey box (scan/crop region guide)
    ctx.save();
    ctx.strokeStyle = 'rgba(180, 180, 180, 0.9)';
    ctx.lineWidth = 2;
    ctx.setLineDash([2, 6]); // Dotted-ish line
    ctx.strokeRect(box.x, box.y, box.width, box.height);
    ctx.restore();

    // Draw inner green guidance box (where QR should be placed to preserve padding for validation)
    const inset = CAMERA_CONFIG.CAPTURE.QR_PADDING;
    const innerX = box.x + inset;
    const innerY = box.y + inset;
    const innerW = box.width - inset * 2;
    const innerH = box.height - inset * 2;

    if (innerW > 8 && innerH > 8) {
      // Green "target" box
      ctx.strokeStyle = `rgba(34, 197, 94, ${opacity})`; // green-500
      ctx.lineWidth = 3;
      ctx.setLineDash([]); // solid
      ctx.strokeRect(innerX, innerY, innerW, innerH);

      // Corner indicators on the inner box
      ctx.lineWidth = 4;
      const cornerSize = 25;

      // Top-left
      ctx.beginPath();
      ctx.moveTo(innerX, innerY + cornerSize);
      ctx.lineTo(innerX, innerY);
      ctx.lineTo(innerX + cornerSize, innerY);
      ctx.stroke();

      // Top-right
      ctx.beginPath();
      ctx.moveTo(innerX + innerW - cornerSize, innerY);
      ctx.lineTo(innerX + innerW, innerY);
      ctx.lineTo(innerX + innerW, innerY + cornerSize);
      ctx.stroke();

      // Bottom-left
      ctx.beginPath();
      ctx.moveTo(innerX, innerY + innerH - cornerSize);
      ctx.lineTo(innerX, innerY + innerH);
      ctx.lineTo(innerX + cornerSize, innerY + innerH);
      ctx.stroke();

      // Bottom-right
      ctx.beginPath();
      ctx.moveTo(innerX + innerW - cornerSize, innerY + innerH);
      ctx.lineTo(innerX + innerW, innerY + innerH);
      ctx.lineTo(innerX + innerW, innerY + innerH - cornerSize);
      ctx.stroke();
    }

    // Add instruction text
    ctx.fillStyle = (color === 'green') ? 'rgba(34, 197, 94, 1)' : 'rgba(200, 200, 200, 0.95)';
    ctx.font = '16px Arial';
    ctx.textAlign = 'center';

    if (color === 'red') {
      ctx.fillText('Keep QR inside the green box', box.x + box.width / 2, box.y - 10);
    } else {
      ctx.fillText('QR Code Detected!', box.x + box.width / 2, box.y - 10);
    }
  }



  // Clear QR code border and redraw detection box
  private clearQRCodeBorder(): void {
    const qrCanvas = this.qrCanvas();
    const qrCtx = this.qrContext();

    if (!qrCanvas || !qrCtx) {
      return;
    }

    // Clear the overlay canvas
    qrCtx.clearRect(0, 0, qrCanvas.width, qrCanvas.height);



    // Redraw the detection box with current color (green if QR detected, red if not)
    this.drawQRDetectionBox();
  }



  // Convert base64 to file
  private base64ToFile(base64: string, filename: string, mimeType: string): File {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new File([byteArray], filename, { type: mimeType });
  }

  // Stop camera feed without resetting all states (used during validation)
  private stopCameraFeed(): void {
    // Stop QR detection loop
    this.stopQRDetectionLoop();
    this.isScanning.set(false);

    // Stop the camera stream
    const stream = this.stream();
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      this.stream.set(null);
    }
    if (this.videoElementRef) {
      this.videoElementRef.nativeElement.srcObject = null;
    }
    this.videoElement.set(null);
    this.isCameraActive.set(false);
  }

  stopCamera(): void {
    // Stop all scanning and processing states
    this.isScanning.set(false);
    this.isProcessing.set(false);
    this.scanCompleted.set(false);
    this.isUploading.set(false);
    this.uploadRetryCount.set(0);
    this.isRetrying.set(false);
    // Reset detected QR code - no longer needed since we use state
    this.qrDetectionTriggered.set(false); // Reset detection trigger flag



    // Stop QR detection loop
    this.stopQRDetectionLoop();



    // Actually stop the camera stream
    const stream = this.stream();
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      this.stream.set(null);
    }
    if (this.videoElementRef) {
      this.videoElementRef.nativeElement.srcObject = null;
    }
    this.videoElement.set(null);
    this.isCameraActive.set(false);
  }



  // Auto-start camera when permission is granted
  onCameraPermissionGranted(): void {
    this.showCameraPermissionPopup.set(false);
    this.requestCameraPermission();
  }

  // Close camera permission popup
  closeCameraPermissionPopup(): void {
    this.showCameraPermissionPopup.set(false);
  }

  // Refresh page to apply camera permissions
  refreshPage(): void {
    window.location.reload();
  }




  // Public method to manually restart QR detection
  public restartDetection(): void {
    // Don't restart if tag is not found - user should be redirected
    if (this.isTagNotFound()) {
      return;
    }

    this.restartQRDetectionLoop();
  }




  // Reset upload state when component is destroyed
  ngOnDestroy(): void {
    this.stopCamera();
    this.store.dispatch(new ResetUploadState());

    // Clean up ZXing reader

    // Remove window resize listener
    window.removeEventListener('resize', this.handleResize.bind(this));

    // Clear any pending resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }


    // Clear idle timeout timers
    this.stopIdleTimeout();
    this.stopQrDetectionTimeout();

    // Reset redirect state
    this.isRedirecting.set(false);

  }

  // Torch/Flashlight functionality
  async toggleTorch(): Promise<void> {
    try {
      if (!this.currentTrack) {
        return;
      }

      if (this.isTorchOn()) {
        await this.turnOffTorch();
      } else {
        await this.turnOnTorch();
      }
    } catch (error) {
      console.error('Torch toggle error:', error);
      // Show user-friendly error message
      this.detectionStatus.set('Flashlight error - try again');
      setTimeout(() => {
        this.detectionStatus.set('Scanning...');
      }, 2000);
    }
  }



  async turnOnTorch(): Promise<void> {
    try {
      if (!this.currentTrack || !this.isTorchSupported()) {
        return;
      }

      const capabilities = this.currentTrack.getCapabilities();
      if ((capabilities as any).torch) {
        await this.currentTrack.applyConstraints({
          advanced: [{ torch: true } as any]
        });
        this.isTorchOn.set(true);
        this.detectionStatus.set('Flashlight ON - scanning with light');
      }
    } catch (error) {
      console.error('Error turning on torch:', error);
      this.detectionStatus.set('Flashlight error - try again');
      setTimeout(() => {
        this.detectionStatus.set('Scanning...');
      }, 2000);
    }
  }

  async turnOffTorch(): Promise<void> {
    try {
      if (!this.currentTrack) {
        return;
      }

      const capabilities = this.currentTrack.getCapabilities();
      if ((capabilities as any).torch) {
        await this.currentTrack.applyConstraints({
          advanced: [{ torch: false } as any]
        });
        this.isTorchOn.set(false);
        this.detectionStatus.set('Flashlight OFF - scanning normally');
        setTimeout(() => {
          this.detectionStatus.set('Scanning...');
        }, 1500);
      }
    } catch (error) {
      console.error('Error turning off torch:', error);
    }
  }

  // Turn off torch silently (without status messages) - used during validation
  private async turnOffTorchSilently(): Promise<void> {
    try {
      if (!this.currentTrack) {
        return;
      }

      const capabilities = this.currentTrack.getCapabilities();
      if ((capabilities as any).torch) {
        await this.currentTrack.applyConstraints({
          advanced: [{ torch: false } as any]
        });
        this.isTorchOn.set(false);
      }
    } catch (error) {
      // Silently fail during validation
    }
  }

  private checkTorchSupport(): void {
    try {
      const stream = this.stream();
      if (stream) {
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          this.currentTrack = videoTrack;
          const capabilities = videoTrack.getCapabilities();
          const hasTorch = !!(capabilities as any).torch;
          this.isTorchSupported.set(hasTorch);

          // Check zoom support
          this.checkZoomSupport(videoTrack);
        } else {
          this.isTorchSupported.set(false);
          this.isZoomSupported.set(false);
        }
      } else {
        this.isTorchSupported.set(false);
        this.isZoomSupported.set(false);
      }
    } catch (error) {
      console.error('Error checking torch support:', error);
      this.isTorchSupported.set(false);
      this.isZoomSupported.set(false);
    }
  }

  // Check if camera supports zoom functionality
  private checkZoomSupport(videoTrack: MediaStreamTrack): void {
    try {
      const capabilities = videoTrack.getCapabilities();
      const zoomCapabilities = (capabilities as any).zoom;

      if (zoomCapabilities) {
        this.isZoomSupported.set(true);
        this.minZoom.set(zoomCapabilities.min || 1.0);
        this.maxZoom.set(zoomCapabilities.max || 4.0);

        // Set default zoom for better small QR detection
        const defaultZoom = Math.min(this.defaultZoom(), this.maxZoom());
        this.defaultZoom.set(defaultZoom);
        this.currentZoom.set(defaultZoom);

        // Show zoom controls if supported
        this.showZoomControls.set(true);
      } else {
        this.isZoomSupported.set(false);
        this.showZoomControls.set(false);
      }
    } catch (error) {
      console.error('Error checking zoom support:', error);
      this.isZoomSupported.set(false);
      this.showZoomControls.set(false);
    }
  }

  // Apply zoom to camera
  async applyZoom(zoomLevel: number): Promise<void> {
    try {
      if (!this.isZoomSupported() || !this.currentTrack) {
        return;
      }

      // Clamp zoom level within supported range
      const clampedZoom = Math.max(this.minZoom(), Math.min(this.maxZoom(), zoomLevel));

      // Apply zoom constraint
      await this.currentTrack.applyConstraints({
        advanced: [{ zoom: clampedZoom } as any]
      });

      this.currentZoom.set(clampedZoom);

      // Update detection status
      this.detectionStatus.set(`Zoom: ${clampedZoom.toFixed(1)}x - Scanning for small QR codes...`);

    } catch (error) {
      console.error('Error applying zoom:', error);
      this.detectionStatus.set('Zoom error - scanning normally');
    }
  }


  // Apply auto-focus and auto-zoom for optimal QR code detection
  async applyAutoFocusAndZoom(): Promise<void> {
    try {
      if (!this.currentTrack) {
        return;
      }

      const capabilities = this.currentTrack.getCapabilities();
      const settings = this.currentTrack.getSettings();

      console.log('Camera capabilities:', capabilities);
      console.log('Current settings:', settings);

      // Build advanced constraints array
      const advancedConstraints: any[] = [];

      // Apply focus mode - Use CONTINUOUS for initial scanning
      // For small QR codes, we'll lock focus after detection
      if ((capabilities as any).focusMode) {
        const modes = (capabilities as any).focusMode;

        if (modes.includes('continuous')) {
          // Use continuous for initial scanning, but optimize for close-up QR codes
          advancedConstraints.push({ focusMode: 'continuous' } as any);
          console.log('Continuous autofocus enabled for initial scanning');

          // Add focusDistance constraint for small QR codes (closer focus distance)
          // This helps the camera focus better on small QR codes that are typically close
          if ((capabilities as any).focusDistance) {
            const focusDist = (capabilities as any).focusDistance;
            // Use optimized ideal distance for small QR codes from config (15-18cm)
            const idealDistance = Math.max(
              focusDist.min || CAMERA_CONFIG.FOCUS.DISTANCE_MIN,
              Math.min(focusDist.max || CAMERA_CONFIG.FOCUS.DISTANCE_MAX_NORMAL, CAMERA_CONFIG.FOCUS.DISTANCE_IDEAL_SMALL_QR)
            );

            advancedConstraints.push({
              focusDistance: {
                ideal: idealDistance,
                min: Math.max(focusDist.min || CAMERA_CONFIG.FOCUS.DISTANCE_MIN, CAMERA_CONFIG.FOCUS.DISTANCE_MIN),
                max: Math.min(focusDist.max || CAMERA_CONFIG.FOCUS.DISTANCE_MAX_NORMAL, CAMERA_CONFIG.FOCUS.DISTANCE_MAX)
              }
            } as any);
            console.log('Focus distance optimized for small QR codes:', idealDistance, 'meters (ideal:', CAMERA_CONFIG.FOCUS.DISTANCE_IDEAL_SMALL_QR, 'm)');
          }
        } else if (modes.includes('single-shot')) {
          // Fallback: Focus once then lock
          advancedConstraints.push({ focusMode: 'single-shot' } as any);
          console.log('Single-shot focus enabled');
        } else {
          console.log('No advanced focus modes available');
        }
      }

      // Apply exposure mode if supported
      if ((capabilities as any).exposureMode && (capabilities as any).exposureMode.includes('continuous')) {
        advancedConstraints.push({ exposureMode: 'continuous' } as any);
        console.log('Continuous exposure mode added');
      }

      // Apply white balance mode if supported
      if ((capabilities as any).whiteBalanceMode && (capabilities as any).whiteBalanceMode.includes('continuous')) {
        advancedConstraints.push({ whiteBalanceMode: 'continuous' } as any);
        console.log('Continuous white balance added');
      }

      // Apply all advanced constraints if any were added
      if (advancedConstraints.length > 0) {
        try {
          await this.currentTrack.applyConstraints({
            advanced: advancedConstraints
          });
          console.log('Advanced camera constraints applied successfully');
        } catch (error) {
          console.warn('Some advanced constraints not supported:', error);
        }
      }

      // Apply auto-zoom if supported - use config-based optimal zoom for small QR codes
      if (this.isZoomSupported()) {
        const optimalZoom = Math.min(this.maxZoom(), CAMERA_CONFIG.ZOOM.OPTIMAL_LEVEL_SMALL_QR);
        await this.applyZoom(optimalZoom);
        this.detectionStatus.set(`Auto-focused and zoomed (${optimalZoom.toFixed(1)}x) for small QR scanning...`);
        console.log('Applied zoom for small QR codes:', optimalZoom);
      } else {
        this.detectionStatus.set('Auto-focused for small QR scanning...');
      }

      // Initial exposure adjustment
      await this.adjustExposure();

    } catch (error) {
      console.warn('Auto-focus/zoom setup failed:', error);
      this.detectionStatus.set('Camera ready for QR scanning...');
    }
  }

  // Restore continuous focus mode after capture for continued scanning
  private async restoreContinuousFocus(): Promise<void> {
    try {
      if (!this.currentTrack) {
        return;
      }

      const capabilities = this.currentTrack.getCapabilities();
      const advancedConstraints: any[] = [];

      // Restore continuous focus for next scan
      if ((capabilities as any).focusMode) {
        const modes = (capabilities as any).focusMode;

        if (modes.includes('continuous')) {
          advancedConstraints.push({ focusMode: 'continuous' } as any);

          // Restore focusDistance for small QR codes
          if ((capabilities as any).focusDistance) {
            const focusDist = (capabilities as any).focusDistance;
            const idealDistance = Math.max(
              focusDist.min || CAMERA_CONFIG.FOCUS.DISTANCE_MIN,
              Math.min(focusDist.max || CAMERA_CONFIG.FOCUS.DISTANCE_MAX_NORMAL, CAMERA_CONFIG.FOCUS.DISTANCE_IDEAL_SMALL_QR)
            );

            advancedConstraints.push({
              focusDistance: {
                ideal: idealDistance,
                min: Math.max(focusDist.min || CAMERA_CONFIG.FOCUS.DISTANCE_MIN, CAMERA_CONFIG.FOCUS.DISTANCE_MIN),
                max: Math.min(focusDist.max || CAMERA_CONFIG.FOCUS.DISTANCE_MAX_NORMAL, CAMERA_CONFIG.FOCUS.DISTANCE_MAX)
              }
            } as any);
          }
        }
      }

      // Restore continuous exposure and white balance
      if ((capabilities as any).exposureMode && (capabilities as any).exposureMode.includes('continuous')) {
        advancedConstraints.push({ exposureMode: 'continuous' } as any);
      }

      if ((capabilities as any).whiteBalanceMode && (capabilities as any).whiteBalanceMode.includes('continuous')) {
        advancedConstraints.push({ whiteBalanceMode: 'continuous' } as any);
      }

      if (advancedConstraints.length > 0) {
        try {
          await this.currentTrack.applyConstraints({
            advanced: advancedConstraints
          });
          console.log('Continuous focus restored for next scan');
        } catch (error) {
          console.warn('Failed to restore continuous focus:', error);
        }
      }
    } catch (error) {
      console.warn('Restore continuous focus failed:', error);
    }
  }

  // Lock focus after QR detection to prevent camera movement and improve stability
  // This is critical for small QR codes that need stable focus
  private async lockFocusForCapture(): Promise<void> {
    try {
      if (!this.currentTrack) {
        return;
      }

      const capabilities = this.currentTrack.getCapabilities();
      const advancedConstraints: any[] = [];

      // Strong focus lock for sharp images - use single-shot to focus once then lock
      // Single-shot is safe and won't cause black screen like manual mode
      if ((capabilities as any).focusMode) {
        const modes = (capabilities as any).focusMode;

        // Use single-shot for focus lock (focuses once then locks - perfect for sharp capture)
        if (modes.includes('single-shot')) {
          advancedConstraints.push({ focusMode: 'single-shot' } as any);
          console.log('Focus locked: single-shot mode for sharp capture');
        }
        // If single-shot not available, keep continuous but ensure it's actively focusing
        // We'll wait longer for continuous mode to settle and get sharp
        else if (modes.includes('continuous')) {
          // Keep continuous mode - it will continue focusing
          // We'll wait longer to ensure it settles
          console.log('Focus: using continuous mode - will wait for focus to settle');
        }
      }

      // For continuous mode, we can also try to get current focus distance and maintain it
      // This helps prevent focus hunting during capture
      if ((capabilities as any).focusDistance && !advancedConstraints.some((c: any) => c.focusMode === 'single-shot')) {
        const settings = this.currentTrack.getSettings();
        const currentFocusDist = (settings as any).focusDistance;
        if (currentFocusDist) {
          // Maintain current focus distance to prevent hunting
          const focusDist = (capabilities as any).focusDistance;
          advancedConstraints.push({
            focusDistance: {
              ideal: currentFocusDist,
              min: Math.max(focusDist.min || 0.1, currentFocusDist * 0.8),
              max: Math.min(focusDist.max || 0.6, currentFocusDist * 1.2)
            }
          } as any);
          console.log('Focus distance maintained at:', currentFocusDist, 'to prevent hunting');
        }
      }

      // Preserve current zoom level to prevent camera from moving closer
      // Do NOT change zoom when locking focus
      if (this.isZoomSupported() && this.currentZoom() > 0) {
        const settings = this.currentTrack.getSettings();
        const currentZoom = (settings as any).zoom || this.currentZoom();
        if (currentZoom) {
          advancedConstraints.push({ zoom: currentZoom } as any);
          console.log('Zoom preserved at:', currentZoom, 'to prevent camera movement');
        }
      }

      // DO NOT lock exposure or white balance to manual mode
      // Manual mode requires setting specific values which can cause black screen
      // Keep them in continuous mode to maintain proper exposure

      // Apply focus lock constraints (without changing zoom or focusDistance)
      if (advancedConstraints.length > 0) {
        try {
          await this.currentTrack.applyConstraints({
            advanced: advancedConstraints
          });
          console.log('Focus locked successfully without changing camera position');

          // Verify video is still playing after applying constraints
          const video = this.videoElement();
          if (video && video.readyState >= 2) {
            // Video is still active
            console.log('Video stream verified active after focus lock');
          } else {
            console.warn('Video stream may have stopped after focus lock');
          }
        } catch (error) {
          console.warn('Some focus lock constraints not supported:', error);
          // If focus lock fails, continue anyway - don't block capture
        }
      }

      // Give the camera time to focus and stabilize - OPTIMIZED for clarity
      // Increased delays slightly to ensure focus is fully locked for sharp images
      // Adjust exposure based on scene brightness (dark or bright)
      await this.adjustExposure();

      const hasSingleShot = advancedConstraints.some((c: any) => c.focusMode === 'single-shot');
      // Use config-based delays - longer delays for small QR codes to ensure sharp focus
      const focusDelay = hasSingleShot
        ? CAMERA_CONFIG.FOCUS.SINGLE_SHOT_DELAY_MS
        : CAMERA_CONFIG.FOCUS.LOCK_DELAY_MS;
      await new Promise(resolve => setTimeout(resolve, focusDelay));
      console.log(`Focus stabilization complete (${focusDelay}ms delay) for small QR code`);

    } catch (error) {
      console.warn('Focus lock failed, continuing with capture:', error);
    }
  }

  // Adjust exposure compensation based on scene brightness
  private async adjustExposure(): Promise<void> {
    try {
      if (!this.currentTrack) return;

      const video = this.videoElement();
      const canvas = this.canvasElement();
      const ctx = this.context();

      if (!video || !canvas || !ctx) return;

      // Quick sample of current frame to detect brightness
      const sampleCanvas = document.createElement('canvas');
      sampleCanvas.width = CAMERA_CONFIG.EXPOSURE.SAMPLE_WIDTH;
      sampleCanvas.height = CAMERA_CONFIG.EXPOSURE.SAMPLE_HEIGHT;
      const sampleCtx = sampleCanvas.getContext('2d', { willReadFrequently: true });
      if (!sampleCtx) return;

      sampleCtx.drawImage(video, 0, 0, sampleCanvas.width, sampleCanvas.height);
      const sampleData = sampleCtx.getImageData(0, 0, sampleCanvas.width, sampleCanvas.height);
      const data = sampleData.data;

      // Calculate average brightness
      let totalBrightness = 0;
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        totalBrightness += (0.299 * r + 0.587 * g + 0.114 * b);
      }

      const avgBrightness = totalBrightness / (data.length / 4);

      // Check capabilities
      const capabilities = this.currentTrack.getCapabilities();
      if (!(capabilities as any).exposureCompensation) {
        return;
      }

      const expComp = (capabilities as any).exposureCompensation;
      const minComp = expComp.min || -3;
      const maxComp = expComp.max || 3;

      let targetExposure: number = CAMERA_CONFIG.EXPOSURE.COMPENSATION_NEUTRAL;
      let sceneType = 'Neutral';

      // Determine target exposure based on brightness thresholds
      if (avgBrightness < CAMERA_CONFIG.EXPOSURE.BRIGHTNESS_THRESHOLD_DARK) {
        // Dark scene: reduce exposure to prevent noise/blooming (as per original logic)
        targetExposure = CAMERA_CONFIG.EXPOSURE.COMPENSATION_DARK;
        sceneType = 'Dark';
      } else if (avgBrightness < CAMERA_CONFIG.EXPOSURE.BRIGHTNESS_THRESHOLD_MODERATE) {
        // Moderately dark
        targetExposure = CAMERA_CONFIG.EXPOSURE.COMPENSATION_MODERATE;
        sceneType = 'Moderately Dark';
      } else if (avgBrightness > CAMERA_CONFIG.EXPOSURE.BRIGHTNESS_THRESHOLD_BRIGHT) {
        // Bright scene: reduce exposure to prevent overexposure/washout
        targetExposure = CAMERA_CONFIG.EXPOSURE.COMPENSATION_BRIGHT;
        sceneType = 'Bright';
      }

      // Clamp to supported range
      targetExposure = Math.max(minComp, Math.min(maxComp, targetExposure));

      try {
        await this.currentTrack.applyConstraints({
          advanced: [{ exposureCompensation: targetExposure } as any]
        });
        console.log(`${sceneType} scene detected (brightness: ${avgBrightness.toFixed(1)}) - adjusted exposure to ${targetExposure}`);
      } catch (error) {
        console.warn(`Failed to adjust exposure for ${sceneType} scene:`, error);
      }

    } catch (error) {
      console.warn('Exposure adjustment failed:', error);
    }
  }

  // ==================== IDLE TIMEOUT MANAGEMENT ====================

  /**
   * Start idle timeout monitoring
   * Automatically closes camera after configured idle time
   */
  startIdleTimeout(): void {
    this.resetIdleTimer();
  }

  /**
   * Reset idle timer - call this on any user activity
   */
  resetIdleTimer(): void {
    this.lastActivityTime = Date.now();
    this.showIdleWarning.set(false);

    // Clear existing timers
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
    if (this.idleWarningTimer) {
      clearTimeout(this.idleWarningTimer);
    }

    // Set warning timer (shows warning before closing)
    const warningTime = CAMERA_CONFIG.TIMEOUTS.IDLE_TIMEOUT_MS - CAMERA_CONFIG.TIMEOUTS.IDLE_WARNING_MS;
    this.idleWarningTimer = setTimeout(() => {
      this.showIdleWarning.set(true);
      this.startWarningCountdown();
    }, warningTime);

    // Set idle timer (closes camera)
    this.idleTimer = setTimeout(() => {
      this.handleIdleTimeout();
    }, CAMERA_CONFIG.TIMEOUTS.IDLE_TIMEOUT_MS);
  }

  /**
   * Start countdown for idle warning
   */
  private startWarningCountdown(): void {
    const updateInterval = 1000; // Update every second
    let remaining = CAMERA_CONFIG.TIMEOUTS.IDLE_WARNING_MS / 1000; // Convert to seconds

    this.idleTimeRemaining.set(remaining);

    const countdownInterval = setInterval(() => {
      remaining--;
      this.idleTimeRemaining.set(remaining);

      if (remaining <= 0 || !this.showIdleWarning()) {
        clearInterval(countdownInterval);
      }
    }, updateInterval);
  }

  /**
   * Handle idle timeout - close camera and show message
   */
  private handleIdleTimeout(): void {
    console.log('Camera idle timeout - closing camera');

    // Stop camera and detection
    this.stopQRDetectionLoop();
    this.stopCamera();

    // Update status
    this.detectionStatus.set('Camera closed due to inactivity');
    this.showIdleWarning.set(false);

    // Show message to user (could add a specific popup here)
    // For now, user can refresh to restart
  }

  /**
   * Stop idle timeout monitoring
   */
  stopIdleTimeout(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.idleWarningTimer) {
      clearTimeout(this.idleWarningTimer);
      this.idleWarningTimer = null;
    }
    this.showIdleWarning.set(false);
  }

  /**
   * Update activity timestamp - call on user interactions
   */
  updateActivity(): void {
    this.resetIdleTimer();
  }

  // ==================== QR DETECTION TIMEOUT MANAGEMENT ====================

  /**
   * Start QR detection timeout
   */
  private startQrDetectionTimeout(): void {
    this.stopQrDetectionTimeout();
    this.qrDetectionTimer = setTimeout(() => {
      this.handleQrDetectionTimeout();
    }, CAMERA_CONFIG.TIMEOUTS.QR_DETECTION_TIMEOUT_MS);
  }

  /**
   * Stop QR detection timeout
   */
  private stopQrDetectionTimeout(): void {
    if (this.qrDetectionTimer) {
      clearTimeout(this.qrDetectionTimer);
      this.qrDetectionTimer = null;
    }
  }

  /**
   * Handle QR detection timeout
   */
  private handleQrDetectionTimeout(): void {
    console.log('[Scanner] QR detection timeout - no QR code detected for 30 seconds');

    // Stop camera and detection
    this.stopQRDetectionLoop();
    this.stopCamera();

    // Navigate to home with timeout query param
    this.router.navigate(['/'], {
      queryParams: { timeout: 'true' },
      replaceUrl: true
    });
  }

  /**
   * Auto-adjust zoom when QR code is detected for better quality
   */
  async autoAdjustZoomForQR(): Promise<void> {
    if (!this.isZoomSupported() || !this.currentTrack) {
      return;
    }

    try {
      // Apply optimal zoom for QR code detection (2.5x for better quality)
      const optimalZoom = Math.min(this.maxZoom(), 2.5);
      await this.applyZoom(optimalZoom);

      this.detectionStatus.set('Auto-zoomed for better QR quality...');
    } catch (error) {
      console.warn('Auto-zoom adjustment failed:', error);
    }
  }

  // Auto-zoom for small QR codes (legacy method - now handled by applyAutoFocusAndZoom)
  async autoZoomForSmallQR(): Promise<void> {
    if (!this.isZoomSupported()) {
      return;
    }

    // Apply optimal zoom for small QR code detection
    const optimalZoom = Math.min(this.maxZoom(), 2.0); // 2x zoom is often optimal
    await this.applyZoom(optimalZoom);

    this.detectionStatus.set('Auto-zoomed for small QR codes - scanning...');
  }
} 